<template>
  <div
    class="product-detail-page"
    :class="{ 'product-detail-page--preview': isCatalogMaster && catalogPreviewOpen }"
  >
    <div class="container container--fluid">
      <Breadcrumb :items="breadcrumbs" class="product-detail-page__breadcrumb" />

      <PageHeader
        :title="
          isCatalogMaster
            ? productForm.title || 'Masterproduct'
            : actualIsNewProduct
              ? 'Nieuw product'
              : productForm.title || 'Product bewerken'
        "
        :description="
          isCatalogMaster
            ? catalogMasterDescription
            : actualIsNewProduct
              ? 'Voeg een nieuw product toe aan je feed'
              : 'Configureer dit product en alle bijbehorende opties'
        "
        break-mobile
      >
        <template #actions>
          <Btn
            v-if="!actualIsNewProduct"
            variant="secondary"
            @click="bulkUpdateCurrentProductPrice"
            type="button"
          >
            Prijsupdate
          </Btn>
          <Btn
            v-if="isCatalogMaster"
            variant="secondary"
            type="button"
            @click="catalogPreviewOpen = !catalogPreviewOpen"
          >
            {{ catalogPreviewOpen ? 'Voorbeeld sluiten' : 'Voorbeeld' }}
          </Btn>
          <Btn
            v-if="isCatalogMaster"
            variant="secondary"
            @click="publishCatalogMaster"
            :loading="publishingCatalog"
            type="button"
          >
            Publiceren
          </Btn>
          <Btn variant="primary" @click="saveProduct" :loading="saving" type="button">
            {{ actualIsNewProduct ? 'Product aanmaken' : 'Wijzigingen opslaan' }}
          </Btn>
        </template>
      </PageHeader>

      <div class="product-detail-page__content">
        <form class="product-form" @submit.prevent="saveProduct">
          <!-- Tab Navigation -->
          <Tabs v-model="activeTab" display="pill" size="md" class="product-tabs">
            <!-- Basis Tab -->
            <Tab name="basis" title="Basis" icon="edit">
              <div class="tab-content">
                <div class="row">
                  <div class="col-lg-6">
                    <Card title="Basis Informatie">
                      <div class="form-section">
                        <div class="form-row">
                          <TextInput
                            :id="inputs.title.id"
                            :label="inputs.title.label"
                            v-model="inputs.title.binding"
                            :error="inputs.title.error"
                            :rules="inputs.title.rules"
                            :placeholder="inputs.title.placeholder"
                            @validate="onValidate('title', $event)"
                          />
                          <TextInput
                            :id="inputs.url.id"
                            :label="inputs.url.label"
                            v-model="inputs.url.binding"
                            :error="inputs.url.error"
                            :rules="inputs.url.rules"
                            :placeholder="inputs.url.placeholder"
                            @validate="onValidate('url', $event)"
                          />
                        </div>

                        <div class="form-row">
                          <TextAreaInput
                            :id="inputs.description.id"
                            :label="inputs.description.label"
                            v-model="inputs.description.binding"
                            :error="inputs.description.error"
                            :rules="inputs.description.rules"
                            :placeholder="inputs.description.placeholder"
                            @validate="onValidate('description', $event)"
                          />
                          <div class="file-input-wrapper">
                            <FileInput
                              :id="inputs.imageUrl.id"
                              :label="inputs.imageUrl.label"
                              v-model="inputs.imageUrl.binding"
                              :error="inputs.imageUrl.error"
                              :rules="inputs.imageUrl.rules"
                              @validate="onValidate('imageUrl', $event)"
                              :upload-config="{
                                type: 'product_image',
                                context: 'product',
                                productId: productForm.id || 'new',
                              }"
                              accept="image/*"
                              @click.stop
                            />
                          </div>
                        </div>

                        <div
                          class="product-basis-toggle"
                          :class="{ 'catalog-locked': catalogInstallationId }"
                        >
                          <SelectInput
                            id="product_display_basis"
                            label="Ik voer prijzen in"
                            :modelValue="productForm.displayBasis"
                            :options="productDisplayBasisOptions"
                            :disabled="!!catalogInstallationId"
                            @update:modelValue="handleProductDisplayBasisChange"
                          />
                          <p class="help-text">
                            <template v-if="catalogInstallationId">
                              Komt uit de prijslijst van de leverancier; je verkoopprijs stel je in
                              bij "Jouw afspraak".
                            </template>
                            <template v-else>
                              Alle prijsvelden in dit product tonen nu
                              {{ productForm.displayBasis === 'incl' ? 'incl. BTW' : 'excl. BTW' }}.
                            </template>
                          </p>
                        </div>

                        <div class="form-row">
                          <div :class="{ 'catalog-locked': catalogInstallationId }">
                            <CurrencyInput
                              id="product_price"
                              :label="productPriceLabel"
                              v-model="inputs.price.binding"
                              :error="inputs.price.error"
                              :rules="inputs.price.rules"
                              @validate="onValidate('price', $event)"
                            />
                          </div>
                          <CurrencyInput
                            id="product_sale_price"
                            :label="productSalePriceLabel"
                            v-model="inputs.salePrice.binding"
                            :error="inputs.salePrice.error"
                            :rules="inputs.salePrice.rules"
                            @validate="onValidate('salePrice', $event)"
                          />
                        </div>

                        <div class="form-row">
                          <TextInput
                            id="product_base_sku"
                            label="Basis SKU"
                            v-model="inputs.baseSku.binding"
                            :error="inputs.baseSku.error"
                            :rules="inputs.baseSku.rules"
                            placeholder="ROL"
                            @validate="onValidate('baseSku', $event)"
                          />
                          <SelectInput
                            id="product_supplier"
                            label="Leverancier"
                            v-model="productForm.supplierId"
                            :options="supplierOptions"
                            placeholder="Selecteer een leverancier..."
                          />
                        </div>

                        <div class="form-row">
                          <MultiSelectInput
                            id="product_categories"
                            label="Categorieën"
                            v-model="productForm.categoryIds"
                            :options="categoryOptions"
                            :allow-create="true"
                            create-placeholder="Nieuwe categorie naam..."
                            create-button-text="Nieuwe categorie toevoegen"
                            manage-button-text="Categorieën beheren"
                            placeholder="Selecteer categorieën..."
                            @create-option="handleCreateCategory"
                            @manage-options="openCategoryModal"
                          />
                        </div>
                      </div>
                    </Card>
                  </div>

                  <div class="col-lg-6">
                    <Card title="Extra informatie">
                      <div class="form-section">
                        <div class="form-row">
                          <NumberInput
                            id="product_standard_hours"
                            label="Normuren"
                            type="text"
                            inputmode="decimal"
                            placeholder="bijv. 1,5"
                            v-model="inputs.standardHours.binding"
                            :error="inputs.standardHours.error"
                            :rules="inputs.standardHours.rules"
                            info="Standaard montagetijd voor dit product. Wordt opgeteld bij de werkbonduur, ook als je maar een deel van de producten selecteert."
                            @blur="normalizeStandardHoursInput"
                            @validate="onValidate('standardHours', $event)"
                          />
                          <SelectInput
                            id="product_unit"
                            :label="$t('products.import.fields.unit')"
                            v-model="productForm.unit"
                            :options="unitOptions"
                          />
                        </div>

                        <div class="form-row">
                          <TextAreaInput
                            id="product_production_notes"
                            label="Productie notities"
                            v-model="inputs.productionNotes.binding"
                            :error="inputs.productionNotes.error"
                            :rules="inputs.productionNotes.rules"
                            placeholder="Speciale instructies voor productie, montage, of materialen..."
                            @validate="onValidate('productionNotes', $event)"
                          />
                          <TextAreaInput
                            id="product_proposal_notes"
                            label="Offerte notities"
                            v-model="inputs.proposalNotes.binding"
                            :error="inputs.proposalNotes.error"
                            :rules="inputs.proposalNotes.rules"
                            placeholder="Notities die op de proposal verschijnen..."
                            @validate="onValidate('proposalNotes', $event)"
                          />
                        </div>

                        <div class="product-proposal-attachments">
                          <h4 class="product-proposal-attachments__title">
                            Standaard offerte-bijlagen
                          </h4>
                          <p class="help-text">
                            Deze PDF's worden automatisch meegestuurd met een offerte waarin dit
                            product zit (ook als het product meerdere keren voorkomt, wordt elke PDF
                            slechts één keer bijgevoegd).
                          </p>
                          <ul v-if="productForm.proposalAttachments.length" class="attachment-list">
                            <li
                              v-for="(att, index) in productForm.proposalAttachments"
                              :key="att.id || att.url || index"
                              class="attachment-list__item"
                            >
                              <BaseIcon name="file" :size="14" />
                              <a
                                :href="att.url"
                                target="_blank"
                                rel="noopener"
                                class="attachment-list__name"
                              >
                                {{ att.name }}
                              </a>
                              <button
                                type="button"
                                class="attachment-list__remove"
                                title="Bijlage verwijderen"
                                @click="removeProposalAttachment(index)"
                              >
                                <BaseIcon name="xmark" :size="14" />
                              </button>
                            </li>
                          </ul>
                          <input
                            ref="proposalAttachmentInputRef"
                            type="file"
                            accept="application/pdf,.pdf"
                            multiple
                            class="attachment-list__input"
                            @change="handleProposalAttachmentUpload"
                          />
                          <Btn
                            text="PDF toevoegen"
                            variant="dashed"
                            icon="plus"
                            :loading="isProposalAttachmentUploading"
                            type="button"
                            @click="triggerProposalAttachmentUpload"
                          />
                        </div>
                      </div>

                      <USPInput
                        id="product_usps"
                        label="Unique Selling Points"
                        v-model="productForm.usps"
                        :max="3"
                        info="Voeg unieke verkoopargumenten toe die je product onderscheiden van de concurrentie"
                      />
                    </Card>

                    <!-- Benodigd materieel (steiger, ladder, ...) -->
                    <Card
                      v-if="!isCatalogMaster && productForm.id"
                      :title="$t('resources.product.title')"
                      class="mt-6"
                    >
                      <ProductInstallationRequirements
                        :product-id="productForm.id"
                        v-model="productForm.installationRequirements"
                      />
                    </Card>

                    <!-- Feed Attributen -->
                    <Card
                      v-if="currentFeed?.attributes?.length"
                      title="Feed Attributen"
                      class="mt-6"
                    >
                      <p class="section-description">
                        Deze attributen worden gebruikt door de feed voor filtering en matching.
                      </p>
                      <div class="attributes-grid">
                        <div
                          v-for="attr in currentFeed.attributes"
                          :key="attr.key"
                          class="attribute-field"
                        >
                          <label>{{ attr.label }} ({{ attr.key }})</label>

                          <!-- String input -->
                          <template v-if="attr.type === 'string'">
                            <input
                              v-model="productForm.attributeValues[attr.key]"
                              :placeholder="attr.label"
                            />
                          </template>

                          <!-- Number input -->
                          <template v-else-if="attr.type === 'number'">
                            <input
                              v-model.number="productForm.attributeValues[attr.key]"
                              type="number"
                              :placeholder="attr.label"
                            />
                          </template>

                          <!-- Select input -->
                          <template v-else-if="attr.type === 'select'">
                            <select v-model="productForm.attributeValues[attr.key]">
                              <option value="">Selecteer {{ attr.label }}</option>
                              <option v-for="option in attr.options" :key="option" :value="option">
                                {{ option }}
                              </option>
                            </select>
                          </template>

                          <!-- Boolean checkbox -->
                          <template v-else-if="attr.type === 'boolean'">
                            <label class="checkbox-label">
                              <input
                                type="checkbox"
                                v-model="productForm.attributeValues[attr.key]"
                              />
                              {{ attr.label }}
                            </label>
                          </template>
                        </div>
                      </div>
                    </Card>

                    <!-- Marge & BTW (Phase 2 margin + VAT feature, 2026-05-12).
                         Lives next to the existing pricing fields on the right
                         column. Backend interprets `price` as a *cost* price
                         when target_margin_pct > 0; otherwise as a sell price. -->
                    <CatalogAgreementCard
                      v-if="catalogInstallationId"
                      :installation-id="catalogInstallationId"
                      :catalog="productForm.attributeValues?.catalog || {}"
                      @saved="onCatalogAgreementSaved"
                    />

                    <Card v-else-if="isCatalogMaster" title="Catalogus & prijsgrondslag" class="mt-6">
                      <div class="form-section">
                        <TextInput
                          id="catalog_category"
                          label="Categorie"
                          v-model="catalogCategory"
                          placeholder="bijv. Zonneschermen"
                          :suggestions="catalogCategoryOptions"
                          info="Alleen voor het groeperen en filteren in de platformcatalogus."
                        />
                      </div>
                      <p class="section-description">
                        Wat de cellen van deze prijslijst zijn. Dealers vullen zelf hun
                        inkoopkorting of marge in; die staat niet in de master.
                      </p>
                      <div class="form-section">
                        <div class="form-row">
                          <div class="margin-field">
                            <SelectInput
                              id="catalog_price_basis"
                              label="Prijzen in deze lijst"
                              v-model="catalogPriceBasis"
                              :options="catalogPriceBasisOptions"
                            />
                            <p class="help-text">{{ catalogPriceBasisHint }}</p>
                          </div>
                          <div class="margin-field">
                            <SelectInput
                              id="product_vat_rate"
                              label="BTW-tarief"
                              v-model="productForm.vatRate"
                              :options="vatRateOptions"
                            />
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card v-else title="Marge & BTW" class="mt-6">
                      <p class="section-description">
                        Bepaal hoe de prijs van dit product wordt behandeld door het
                        pricing-systeem. Lege waardes blijven gelijk aan vandaag — backward
                        compatible.
                      </p>

                      <div class="form-section">
                        <div class="form-row">
                          <div class="margin-field">
                            <NumberInput
                              id="product_target_margin_pct"
                              label="Marge %"
                              v-model="productForm.targetMarginPct"
                              :min="0"
                              :max="95"
                              :step="0.1"
                            />
                            <p class="help-text">
                              De ingevoerde prijs wordt behandeld als
                              <strong>inkoopprijs</strong>
                              . De verkoopprijs wordt automatisch berekend met formule
                              <code>verkoop = inkoop / (1 − marge/100)</code>
                              .
                              <span v-if="marginPreview" class="margin-field__preview">
                                {{ marginPreview }}
                              </span>
                            </p>
                          </div>

                          <div class="margin-field">
                            <NumberInput
                              id="product_min_margin_pct"
                              label="Minimum marge %"
                              v-model="productForm.minMarginPct"
                              :min="0"
                              :max="95"
                              :step="0.1"
                            />
                            <p class="help-text">
                              Het stats-dashboard waarschuwt zodra de gerealiseerde marge op een
                              offerte onder deze drempel zakt.
                            </p>
                            <p v-if="minMarginAboveTarget" class="help-text help-text--warning">
                              <BaseIcon name="exclamation-triangle" :size="12" />
                              Minimum marge ({{ productForm.minMarginPct }}%) ligt boven het target
                              ({{ productForm.targetMarginPct }}%). Elke deal wordt direct als
                              "onder de minimum" gemarkeerd.
                            </p>
                          </div>
                        </div>

                        <div class="form-row">
                          <div class="margin-field">
                            <SelectInput
                              id="product_vat_rate"
                              label="BTW-tarief"
                              v-model="productForm.vatRate"
                              :options="vatRateOptions"
                            />
                            <p class="help-text">
                              Wettelijk BTW-tarief dat op dit product van toepassing is.
                            </p>
                          </div>
                          <div />
                          <!-- Empty cell to keep the 2-col grid balanced -->
                        </div>

                        <div class="margin-advanced">
                          <button
                            type="button"
                            class="margin-advanced__toggle"
                            :class="{ 'margin-advanced__toggle--open': showAdvancedVat }"
                            @click="showAdvancedVat = !showAdvancedVat"
                          >
                            <BaseIcon name="chevron" :size="14" />
                            Geavanceerd
                          </button>

                          <div v-if="showAdvancedVat" class="margin-advanced__body">
                            <label class="vat-basis-option__label">BTW-basis</label>
                            <p class="help-text">
                              Bepaalt hoe het bedrag in het prijs-veld wordt geïnterpreteerd. Laat
                              op "Volg organisatie" staan tenzij dit specifieke product anders moet
                              worden behandeld.
                            </p>

                            <div class="vat-basis-radio">
                              <label
                                v-for="opt in vatBasisOptions"
                                :key="String(opt.value)"
                                class="vat-basis-radio__option"
                                :class="{
                                  'vat-basis-radio__option--checked':
                                    productForm.pricesIncludeVat === opt.value,
                                }"
                              >
                                <input
                                  type="radio"
                                  name="product_prices_include_vat"
                                  :checked="productForm.pricesIncludeVat === opt.value"
                                  @change="handleLegacyProductBasisChange(opt.value)"
                                />
                                <div>
                                  <span class="vat-basis-radio__title">
                                    {{ opt.label }}
                                  </span>
                                  <span class="vat-basis-radio__hint">
                                    {{ opt.hint }}
                                  </span>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </Tab>

            <!-- Configuratie Tab -->
            <Tab
              name="configuratie"
              title="Configuratie"
              icon="settings"
              :badge="configurationCount || null"
            >
              <CatalogAssortmentCard
                v-if="catalogInstallationId"
                class="catalog-assortment-wrap"
                :installation-id="catalogInstallationId"
                @saved="onCatalogRulesSaved"
              />
              <CatalogOwnFieldsCard
                v-if="catalogInstallationId"
                class="catalog-assortment-wrap"
                :installation-id="catalogInstallationId"
                :groups="productForm.optionGroups"
                :options="productForm.options"
                @saved="onCatalogRulesSaved"
              />
              <CatalogLockNotice v-if="catalogInstallationId" />
              <div class="tab-content" :class="{ 'tab-content--locked': catalogInstallationId }">
                <div class="row">
                  <div class="col-lg-12">
                    <Card title="Productopties">
                      <template #actions>
                        <Btn
                          variant="tertiary"
                          @click="copyProductOptionsFromOther"
                          type="button"
                          size="sm"
                          icon="copy"
                        >
                          {{ $t('products.copy_options.button') }}
                        </Btn>
                        <Btn
                          v-if="productForm.options?.length"
                          variant="tertiary"
                          @click="addProductOption"
                          type="button"
                          size="sm"
                          icon="plus"
                        >
                          Optie toevoegen
                        </Btn>
                        <Btn
                          v-if="productForm.options?.length"
                          variant="tertiary"
                          @click="addOptionGroup"
                          type="button"
                          size="sm"
                          icon="folder-plus"
                        >
                          Groep toevoegen
                        </Btn>
                      </template>
                      <EmptyState
                        v-if="!productForm.options?.length"
                        title="Nog geen opties"
                        description="Voeg configuratie opties toe zoals breedte, kleur, bediening, etc."
                      >
                        <div class="empty-state-actions">
                          <Btn
                            variant="secondary"
                            @click="addProductOption"
                            type="button"
                            size="sm"
                            icon="plus"
                          >
                            Optie toevoegen
                          </Btn>
                          <Btn
                            variant="tertiary"
                            @click="copyProductOptionsFromOther"
                            type="button"
                            size="sm"
                            icon="copy"
                          >
                            {{ $t('products.copy_options.button') }}
                          </Btn>
                        </div>
                      </EmptyState>
                      <div v-else class="options-list">
                        <!-- Draggable Groups (for reordering groups) -->
                        <draggable
                          v-model="productForm.optionGroups"
                          class="option-groups-container"
                          item-key="id"
                          handle=".group-drag-handle"
                          ghost-class="option-group--ghost"
                        >
                          <template #item="{ element: group }">
                            <div
                              v-show="
                                !(catalogInstallationId && group.catalog_origin === 'dealer')
                              "
                              class="option-group-section"
                            >
                              <div class="option-group-header">
                                <div class="group-drag-handle">
                                  <BaseIcon name="grip-dots" size="16" />
                                </div>
                                <div class="group-header-left" @click="toggleGroup(group.id)">
                                  <BaseIcon
                                    name="chevron"
                                    size="16"
                                    :class="{ 'icon-rotated': !groupExpanded[group.id] }"
                                  />
                                  <h4>{{ group.name }}</h4>
                                  <span class="option-count">
                                    {{ getOptionsInGroup(group.id).length }}
                                  </span>
                                </div>
                                <div class="group-header-actions">
                                  <Btn
                                    variant="plain"
                                    size="sm"
                                    @click="editOptionGroup(group.id)"
                                    type="button"
                                    icon="edit"
                                  />
                                  <Btn
                                    variant="plain"
                                    size="sm"
                                    @click="removeOptionGroup(group.id)"
                                    type="button"
                                    icon="trash"
                                  />
                                </div>
                              </div>

                              <div v-show="groupExpanded[group.id]" class="option-group-content">
                                <!-- Options in this group -->
                                <div
                                  class="draggable-options-list"
                                  :class="{
                                    'draggable-options-list--empty':
                                      getOptionsInGroup(group.id).length === 0,
                                  }"
                                  @dragover.prevent
                                  @drop="(e) => handleDropOnGroup(group.id, e)"
                                >
                                  <div
                                    v-for="(option, idx) in getOptionsInGroup(group.id)"
                                    :key="option.id"
                                    class="option-card-wrapper"
                                    @dragover="(e) => handleDragOverOption(option.id, e)"
                                    @dragleave="handleDragLeaveOption"
                                    @drop="(e) => handleDropOnOption(option.id, group.id, e)"
                                  >
                                    <div
                                      v-if="
                                        dropIndicator.optionId === option.id &&
                                        dropIndicator.position === 'before'
                                      "
                                      class="drop-indicator"
                                    ></div>
                                    <div
                                      class="option-card"
                                      :class="{
                                        'option-card--dragging': draggingOptionId === option.id,
                                        'option-card--autofill': isAutoFillOn(option),
                                      }"
                                      draggable="true"
                                      @dragstart="(e) => handleDragStart(option, e)"
                                      @dragend="handleDragEnd"
                                    >
                                      <div class="option-card__drag-handle">
                                        <BaseIcon name="grip-dots" size="18" />
                                      </div>
                                      <div class="option-card__icon">
                                        <BaseIcon
                                          :name="`elements_${option.type.replace(/-/g, '_')}`"
                                          size="20"
                                        />
                                      </div>
                                      <div class="option-card__info">
                                        <span class="option-card__name">
                                          {{ option.label || 'Nieuwe optie' }}
                                        </span>
                                        <span class="option-card__description">
                                          {{ getOptionDescription(option) }}
                                        </span>
                                      </div>
                                      <div class="option-card__right">
                                        <button
                                          v-if="isAutoFillEligible(option)"
                                          type="button"
                                          class="option-card__badge option-card__badge--autofill"
                                          :class="{ 'is-on': isAutoFillOn(option) }"
                                          @click.stop="toggleAutoFill(option)"
                                          :title="
                                            (isAutoFillOn(option)
                                              ? 'Auto-fill aan — een keuze hier vult de verwante velden mee, ook de verborgen: '
                                              : 'Auto-fill uit — klik om verwante velden elkaar te laten invullen: ') +
                                            autoFillSiblingLabels(option).join(', ')
                                          "
                                        >
                                          <BaseIcon name="link" size="14" />
                                          Auto-fill
                                        </button>
                                        <span
                                          v-if="option.hideOnQuote"
                                          class="option-card__badge option-card__badge--hidden"
                                        >
                                          Verborgen
                                        </span>
                                        <span
                                          v-if="option.showOnPackingSlip"
                                          class="option-card__badge option-card__badge--packing"
                                        >
                                          Pakbon
                                        </span>
                                        <span
                                          v-if="option.internalOnly"
                                          class="option-card__badge option-card__badge--internal"
                                        >
                                          Intern
                                        </span>
                                        <span
                                          v-if="option.externalOnly"
                                          class="option-card__badge option-card__badge--external"
                                        >
                                          Extern
                                        </span>
                                        <span
                                          v-if="option.required"
                                          class="option-card__badge option-card__badge--required"
                                        >
                                          Verplicht
                                        </span>
                                        <Btn
                                          variant="outlined"
                                          size="lg"
                                          @click.stop="editProductOptionById(option.id)"
                                          type="button"
                                          icon="edit"
                                        />
                                        <Btn
                                          variant="outlined"
                                          size="lg"
                                          @click.stop="removeProductOptionById(option.id)"
                                          type="button"
                                          icon="trash"
                                        />
                                      </div>
                                    </div>
                                    <div
                                      v-if="
                                        dropIndicator.optionId === option.id &&
                                        dropIndicator.position === 'after'
                                      "
                                      class="drop-indicator"
                                    ></div>
                                  </div>
                                </div>
                                <p
                                  v-if="getOptionsInGroup(group.id).length === 0"
                                  class="empty-drop-hint"
                                >
                                  Sleep opties hierheen
                                </p>
                              </div>
                            </div>
                          </template>
                        </draggable>

                        <!-- Ungrouped Options -->
                        <div class="option-group-section option-group-section--ungrouped">
                          <div class="option-group-header option-group-header--ungrouped">
                            <div class="group-header-left">
                              <BaseIcon name="cubes" size="16" />
                              <h4>Losse opties (geen groep)</h4>
                              <span class="option-count">{{ ungroupedOptions.length }}</span>
                            </div>
                          </div>
                          <div class="option-group-content">
                            <div
                              class="draggable-options-list"
                              :class="{
                                'draggable-options-list--empty': ungroupedOptions.length === 0,
                              }"
                              @dragover.prevent
                              @drop="(e) => handleDropOnGroup(null, e)"
                            >
                              <div
                                v-for="(option, idx) in ungroupedOptions"
                                :key="option.id"
                                class="option-card-wrapper"
                                @dragover="(e) => handleDragOverOption(option.id, e)"
                                @dragleave="handleDragLeaveOption"
                                @drop="(e) => handleDropOnOption(option.id, null, e)"
                              >
                                <div
                                  v-if="
                                    dropIndicator.optionId === option.id &&
                                    dropIndicator.position === 'before'
                                  "
                                  class="drop-indicator"
                                ></div>
                                <div
                                  class="option-card"
                                  :class="{
                                    'option-card--dragging': draggingOptionId === option.id,
                                  }"
                                  draggable="true"
                                  @dragstart="(e) => handleDragStart(option, e)"
                                  @dragend="handleDragEnd"
                                >
                                  <div class="option-card__drag-handle">
                                    <BaseIcon name="grip-dots" size="18" />
                                  </div>
                                  <div class="option-card__icon">
                                    <BaseIcon
                                      :name="`elements_${option.type.replace(/-/g, '_')}`"
                                      size="20"
                                    />
                                  </div>
                                  <div class="option-card__info">
                                    <span class="option-card__name">
                                      {{ option.label || 'Nieuwe optie' }}
                                    </span>
                                    <span class="option-card__description">
                                      {{ getOptionDescription(option) }}
                                    </span>
                                  </div>
                                  <div class="option-card__right">
                                    <span
                                      v-if="option.hideOnQuote"
                                      class="option-card__badge option-card__badge--hidden"
                                    >
                                      Verborgen
                                    </span>
                                    <span
                                      v-if="option.showOnPackingSlip"
                                      class="option-card__badge option-card__badge--packing"
                                    >
                                      Pakbon
                                    </span>
                                    <span
                                      v-if="option.internalOnly"
                                      class="option-card__badge option-card__badge--internal"
                                    >
                                      Intern
                                    </span>
                                    <span
                                      v-if="option.externalOnly"
                                      class="option-card__badge option-card__badge--external"
                                    >
                                      Extern
                                    </span>
                                    <span
                                      v-if="option.required"
                                      class="option-card__badge option-card__badge--required"
                                    >
                                      Verplicht
                                    </span>
                                    <Btn
                                      variant="outlined"
                                      size="lg"
                                      @click.stop="editProductOptionById(option.id)"
                                      type="button"
                                      icon="edit"
                                    />
                                    <Btn
                                      variant="outlined"
                                      size="lg"
                                      @click.stop="removeProductOptionById(option.id)"
                                      type="button"
                                      icon="trash"
                                    />
                                  </div>
                                </div>
                                <div
                                  v-if="
                                    dropIndicator.optionId === option.id &&
                                    dropIndicator.position === 'after'
                                  "
                                  class="drop-indicator"
                                ></div>
                              </div>
                            </div>
                            <p v-if="ungroupedOptions.length === 0" class="empty-drop-hint">
                              Sleep opties hierheen om ze los te zetten
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-12">
                    <!-- Upsells -->
                    <Card title="Upsells">
                      <template #actions>
                        <Btn
                          variant="tertiary"
                          @click="addUpsell"
                          type="button"
                          size="sm"
                          icon="plus"
                          iconPosition="left"
                        >
                          Upsell toevoegen
                        </Btn>
                        <Btn
                          variant="tertiary"
                          @click="addUpsellGroup"
                          type="button"
                          size="sm"
                          icon="folder-plus"
                          iconPosition="left"
                        >
                          Groep toevoegen
                        </Btn>
                      </template>
                      <p class="section-description">
                        <template v-if="isCatalogMaster">
                          Extra masterproducten. Bij een import worden die ook in de organisatie
                          gezet, als ze er nog niet staan.
                        </template>
                        <template v-else>
                          Extra producten die de klant kan toevoegen aan de bestelling
                        </template>
                      </p>

                      <div
                        v-if="productForm.upsells?.length || productForm.upsellGroups?.length"
                        class="upsells-list"
                      >
                        <!-- Upsell Groups -->
                        <draggable
                          v-model="productForm.upsellGroups"
                          class="upsell-groups-container"
                          item-key="id"
                          handle=".upsell-group-drag-handle"
                          ghost-class="upsell-group-section--ghost"
                        >
                          <template #item="{ element: group }">
                            <div class="upsell-group-section">
                              <div class="upsell-group-header">
                                <div class="upsell-group-drag-handle">
                                  <BaseIcon name="grip-dots" size="16" />
                                </div>
                                <div
                                  class="upsell-group-header-left"
                                  @click="toggleUpsellGroup(group.id)"
                                >
                                  <BaseIcon
                                    name="chevron"
                                    size="16"
                                    :class="{ 'icon-rotated': !upsellGroupExpanded[group.id] }"
                                  />
                                  <h4>{{ group.name }}</h4>
                                  <span class="upsell-count">
                                    {{ getUpsellsInGroup(group.id).length }}
                                  </span>
                                </div>
                                <div class="upsell-group-header-actions">
                                  <Btn
                                    variant="outlined"
                                    size="lg"
                                    @click="editUpsellGroup(group.id)"
                                    type="button"
                                    icon="edit"
                                  />
                                  <Btn
                                    size="md"
                                    variant="outlined"
                                    @click="removeUpsellGroup(group.id)"
                                    type="button"
                                    icon="trash"
                                  />
                                </div>
                              </div>

                              <div
                                v-show="upsellGroupExpanded[group.id]"
                                class="upsell-group-content"
                              >
                                <div
                                  class="draggable-upsells-list"
                                  :class="{
                                    'draggable-upsells-list--empty':
                                      getUpsellsInGroup(group.id).length === 0,
                                  }"
                                  @dragover.prevent
                                  @drop="(e) => handleDropOnUpsellGroup(group.id, e)"
                                >
                                  <div
                                    v-for="(upsell, idx) in getUpsellsInGroup(group.id)"
                                    :key="getUpsellKey(upsell, idx)"
                                    class="upsell-card-wrapper"
                                    @dragover="(e) => handleDragOverUpsell(upsell.id, e)"
                                    @dragleave="handleDragLeaveUpsell"
                                    @drop="(e) => handleDropOnUpsell(upsell.id, group.id, e)"
                                  >
                                    <div
                                      v-if="
                                        upsellDropIndicator.upsellId === upsell.id &&
                                        upsellDropIndicator.position === 'before'
                                      "
                                      class="drop-indicator drop-indicator--before"
                                    />
                                    <div
                                      class="upsell-card"
                                      :class="{
                                        'upsell-card--dragging': draggingUpsellId === upsell.id,
                                      }"
                                      draggable="true"
                                      @dragstart="(e) => handleUpsellDragStart(upsell, e)"
                                      @dragend="handleUpsellDragEnd"
                                    >
                                      <div class="upsell-card__drag-handle">
                                        <BaseIcon name="grip-dots" size="18" />
                                      </div>
                                      <div class="upsell-card__image">
                                        <img
                                          v-if="upsell.imageUrl"
                                          :src="upsell.imageUrl"
                                          :alt="upsell.name"
                                        />
                                        <BaseIcon v-else name="image" size="24" />
                                      </div>
                                      <div class="upsell-card__info">
                                        <span class="upsell-card__name">
                                          {{ upsell.name || 'Nieuwe upsell' }}
                                        </span>
                                        <span
                                          v-if="upsell.description"
                                          class="upsell-card__description"
                                        >
                                          {{ upsell.description }}
                                        </span>
                                      </div>
                                      <div class="upsell-card__right">
                                        <span class="upsell-card__price">
                                          + € {{ formatUpsellPrice(upsell.price) }}
                                        </span>
                                        <Btn
                                          variant="outline"
                                          size="md"
                                          @click.stop="
                                            editUpsell(productForm.upsells.indexOf(upsell))
                                          "
                                          type="button"
                                          icon="edit"
                                        />
                                        <Btn
                                          variant="outline"
                                          size="md"
                                          @click.stop="
                                            removeUpsell(productForm.upsells.indexOf(upsell))
                                          "
                                          type="button"
                                          icon="trash"
                                        />
                                      </div>
                                    </div>
                                    <div
                                      v-if="
                                        upsellDropIndicator.upsellId === upsell.id &&
                                        upsellDropIndicator.position === 'after'
                                      "
                                      class="drop-indicator drop-indicator--after"
                                    />
                                  </div>

                                  <!-- Drop zone placeholder for empty groups -->
                                  <div
                                    v-if="getUpsellsInGroup(group.id).length === 0"
                                    class="upsell-group-empty"
                                  >
                                    Sleep upsells hierheen
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </draggable>

                        <!-- Ungrouped Upsells -->
                        <div class="upsell-group-section upsell-group-section--ungrouped">
                          <div class="upsell-group-header upsell-group-header--ungrouped">
                            <div class="upsell-group-header-left">
                              <BaseIcon name="cubes" size="16" />
                              <h4>Losse upsells (geen groep)</h4>
                              <span class="upsell-count">{{ ungroupedUpsells.length }}</span>
                            </div>
                          </div>
                          <div class="upsell-group-content">
                            <div
                              class="draggable-upsells-list"
                              :class="{
                                'draggable-upsells-list--empty': ungroupedUpsells.length === 0,
                              }"
                              @dragover.prevent
                              @drop="(e) => handleDropOnUpsellGroup(null, e)"
                            >
                              <div
                                v-for="(upsell, idx) in ungroupedUpsells"
                                :key="getUpsellKey(upsell, idx)"
                                class="upsell-card-wrapper"
                                @dragover="(e) => handleDragOverUpsell(upsell.id, e)"
                                @dragleave="handleDragLeaveUpsell"
                                @drop="(e) => handleDropOnUpsell(upsell.id, null, e)"
                              >
                                <div
                                  v-if="
                                    upsellDropIndicator.upsellId === upsell.id &&
                                    upsellDropIndicator.position === 'before'
                                  "
                                  class="drop-indicator drop-indicator--before"
                                />
                                <div
                                  class="upsell-card"
                                  :class="{
                                    'upsell-card--dragging': draggingUpsellId === upsell.id,
                                  }"
                                  draggable="true"
                                  @dragstart="(e) => handleUpsellDragStart(upsell, e)"
                                  @dragend="handleUpsellDragEnd"
                                >
                                  <div class="upsell-card__drag-handle">
                                    <BaseIcon name="grip-dots" size="18" />
                                  </div>
                                  <div class="upsell-card__image">
                                    <img
                                      v-if="upsell.imageUrl"
                                      :src="upsell.imageUrl"
                                      :alt="upsell.name"
                                    />
                                    <BaseIcon v-else name="image" size="24" />
                                  </div>
                                  <div class="upsell-card__info">
                                    <span class="upsell-card__name">
                                      {{ upsell.name || 'Nieuwe upsell' }}
                                    </span>
                                    <span
                                      v-if="upsell.description"
                                      class="upsell-card__description"
                                    >
                                      {{ upsell.description }}
                                    </span>
                                  </div>
                                  <div class="upsell-card__right">
                                    <span class="upsell-card__price">
                                      + € {{ formatUpsellPrice(upsell.price) }}
                                    </span>
                                    <Btn
                                      variant="outlined"
                                      size="lg"
                                      @click.stop="editUpsell(productForm.upsells.indexOf(upsell))"
                                      type="button"
                                      icon="edit"
                                    />
                                    <Btn
                                      variant="outlined"
                                      size="lg"
                                      @click.stop="
                                        removeUpsell(productForm.upsells.indexOf(upsell))
                                      "
                                      type="button"
                                      icon="trash"
                                    />
                                  </div>
                                </div>
                                <div
                                  v-if="
                                    upsellDropIndicator.upsellId === upsell.id &&
                                    upsellDropIndicator.position === 'after'
                                  "
                                  class="drop-indicator drop-indicator--after"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Btn
                        variant="dashed"
                        @click="addUpsell"
                        type="button"
                        icon="plus"
                        iconPosition="left"
                        class="mt-4"
                      >
                        Nog een upsell toevoegen
                      </Btn>
                    </Card>
                  </div>
                </div>
              </div>
            </Tab>

            <!-- Leverancier Tab -->
            <Tab name="leverancier" title="Leverancier" icon="truck">
              <div class="tab-content" v-if="productForm.supplierId || isCatalogMaster">
                <SupplierMappingTab
                  :product-id="productId || 'catalog'"
                  :supplier-id="productForm.supplierId || 'catalog'"
                  :supplier-name="selectedSupplierName || catalogSupplierName"
                  :product-options="productForm.options || []"
                  :preset-mapping="isCatalogMaster ? catalogOrderMapping : null"
                  :local-only="isCatalogMaster"
                  @save="onCatalogMappingSave"
                />
              </div>
              <div class="tab-content" v-else>
                <EmptyState
                  title="Geen leverancier"
                  description="Selecteer eerst een leverancier bij de basis tab om mappings te configureren."
                />
              </div>
            </Tab>

            <!-- Regels Tab -->
            <Tab name="regels" title="Regels" icon="scale">
              <div class="tab-content">
                <!-- Catalog product: the version's pricing is shown read-only; the
                     three dealer-editable rule kinds live in CatalogRulesTab below. -->
                <CatalogLockNotice
                  v-if="catalogInstallationId"
                  title="Prijsopbouw van de leverancier"
                  :text="catalogPricingNotice"
                />

                <div
                  v-if="catalogInstallationId && productForm.useMatrixPricing"
                  class="catalog-matrix-bar"
                  :class="{ 'catalog-matrix-bar--detached': catalogMatrixDetached }"
                >
                  <div class="catalog-matrix-bar__text">
                    <template v-if="matrixEditing">
                      <strong>Je bewerkt je eigen matrix</strong>
                      <span>
                        Prijzen in {{ catalogBasisLabel }}. De assen blijven die van de leverancier.
                        Na opslaan loopt de matrix niet meer mee met nieuwe prijslijsten.
                      </span>
                    </template>
                    <template v-else-if="catalogMatrixDetached">
                      <Label variant="warning" size="sm">Losgekoppeld</Label>
                      <span>
                        Je gebruikt een eigen matrix. Nieuwe prijslijsten veranderen deze matrix
                        niet.
                      </span>
                    </template>
                    <template v-else>
                      <span>
                        De matrix komt van de leverancier en loopt mee met nieuwe prijslijsten.
                      </span>
                    </template>
                  </div>
                  <div class="catalog-matrix-bar__actions">
                    <template v-if="matrixEditing">
                      <Btn
                        variant="tertiary"
                        size="sm"
                        :disabled="matrixSaving"
                        @click="cancelMatrixEdit"
                      >
                        Annuleren
                      </Btn>
                      <Btn
                        variant="primary"
                        size="sm"
                        :loading="matrixSaving"
                        @click="saveMatrixOverride"
                      >
                        Eigen matrix opslaan
                      </Btn>
                    </template>
                    <template v-else-if="catalogMatrixDetached">
                      <Btn
                        variant="tertiary"
                        size="sm"
                        :loading="matrixSaving"
                        @click="resetMatrixOverride"
                      >
                        Terug naar leverancier
                      </Btn>
                      <Btn variant="secondary" size="sm" @click="startMatrixEdit">Bewerken</Btn>
                    </template>
                    <Btn v-else variant="secondary" size="sm" @click="startMatrixEdit">
                      Eigen matrix gebruiken
                    </Btn>
                  </div>
                </div>

                <!-- Matrix Pricing -->
                <Card
                  title="Matrix Pricing"
                  :subtitle="matrixPricingSubtitle"
                  :class="{ 'catalog-locked': catalogInstallationId && !matrixEditing }"
                >
                  <!-- Toggle Card -->
                  <div
                    class="matrix-toggle-card"
                    :class="{ 'catalog-locked': catalogInstallationId }"
                  >
                    <div class="matrix-toggle-card__content">
                      <div class="matrix-toggle-card__text">
                        <span class="matrix-toggle-card__title">
                          Matrix pricing actief voor dit product
                        </span>
                        <span class="matrix-toggle-card__subtitle">
                          Prijzen worden automatisch berekend op basis van de gekozen opties
                        </span>
                      </div>
                      <ToggleInput
                        id="use_matrix_pricing_toggle"
                        v-model="productForm.useMatrixPricing"
                      />
                    </div>
                  </div>

                  <!-- MatrixPricingEditor (only when enabled) -->
                  <MatrixPricingEditor
                    v-if="productForm.useMatrixPricing"
                    :key="matrixEditorKey"
                    :product="productForm"
                    :lock-dimensions="!!catalogInstallationId"
                    @update="handleMatrixPricingUpdate"
                  />
                </Card>

                <!-- Computed Fields -->
                <Card
                  v-if="productForm.options?.length"
                  title="Berekende velden"
                  class="mt-6"
                  :class="{ 'catalog-locked': catalogInstallationId }"
                  subtitle="Definieer berekeningen op basis van opties (bijv. Lengte × Breedte = m²)"
                >
                  <ComputedFieldsEditor
                    v-model="productForm.computedFields"
                    :product-options="productForm.options || []"
                  />
                </Card>

                <!-- Choice Constraints (shown here for catalog products so the
                     read-only part stays together; normal products keep it last) -->
                <Card
                  v-if="catalogInstallationId && productForm.options?.length"
                  title="Optiebeperkingen"
                  class="mt-6 catalog-locked"
                  subtitle="Welke keuzes leverbaar zijn per maat of combinatie, volgens de prijslijst"
                >
                  <ChoiceConstraintEditor
                    v-model="productForm.choiceConstraints"
                    :product-options="productForm.options || []"
                    :computed-fields="productForm.computedFields || []"
                  />
                </Card>

                <CatalogPriceOverridesCard
                  v-if="catalogInstallationId"
                  class="mt-6"
                  :installation-id="catalogInstallationId"
                  @saved="onCatalogRulesSaved"
                />

                <CatalogRulesTab
                  v-if="catalogInstallationId"
                  class="mt-6"
                  :installation-id="catalogInstallationId"
                  :product-options="productForm.options || []"
                  :computed-fields="productForm.computedFields || []"
                  :product-name="productForm.title || inputs.title.binding || 'dit product'"
                  :basis-field-options="volumeBasisFieldOptions"
                  @saved="onCatalogRulesSaved"
                />

                <!-- Cross-Option Rules -->
                <Card
                  v-if="!catalogInstallationId && productForm.options?.length"
                  title="Cross-Option pricing"
                  class="mt-6"
                  subtitle="Prijsaanpassingen op basis van combinaties van opties"
                >
                  <CrossOptionRulesEditor
                    v-model="productForm.crossOptionRules"
                    :product-options="productForm.options || []"
                    :computed-fields="productForm.computedFields || []"
                  />
                </Card>

                <!-- Volume Pricing Rules -->
                <Card
                  v-if="!catalogInstallationId"
                  title="Aantalregels"
                  class="mt-6"
                  subtitle="Kortingen of toeslagen op basis van hoeveel keer dit product in de offerte staat, of een opgeteld veld zoals totale m²"
                >
                  <VolumePricingRulesEditor
                    v-model="productForm.volumePricingRules"
                    :allowed-scopes="['same_product']"
                    :product-name="productForm.title || inputs.title.binding || 'dit product'"
                    :basis-field-options="volumeBasisFieldOptions"
                  />
                </Card>

                <!-- Auto-Include Rules -->
                <Card
                  v-if="!catalogInstallationId"
                  title="Auto-Include items"
                  class="mt-6"
                  subtitle="Items die automatisch meegeleverd worden bij dit product"
                >
                  <AutoIncludeRulesEditor
                    v-model="productForm.autoIncludeRules"
                    :product-options="productForm.options || []"
                    :computed-fields="productForm.computedFields || []"
                  />
                </Card>

                <!-- Choice Constraints -->
                <Card
                  v-if="!catalogInstallationId && productForm.options?.length"
                  title="Optiebeperkingen"
                  class="mt-6"
                  subtitle="Beperk welke keuzes beschikbaar zijn op basis van andere opties (bijv. kastmaat op basis van hoogte &times; breedte)"
                >
                  <ChoiceConstraintEditor
                    v-model="productForm.choiceConstraints"
                    :product-options="productForm.options || []"
                    :computed-fields="productForm.computedFields || []"
                  />
                </Card>
              </div>
            </Tab>
          </Tabs>
        </form>
      </div>
    </div>
    <aside v-if="isCatalogMaster && catalogPreviewOpen" class="product-detail-page__preview">
      <p class="product-detail-page__preview-note">
        Zo ziet de klant het formulier. De prijs is de brochureprijs van dit concept, ook als je
        nog niet hebt opgeslagen.
      </p>
      <ProductConfiguratorForm
        preview
        audience="customer"
        :product="catalogPreviewProduct"
      />
    </aside>
  </div>
</template>

<script>
  import {
    defineComponent,
    computed,
    reactive,
    ref,
    watch,
    onMounted,
    nextTick,
    getCurrentInstance,
  } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useValidation } from '@/composables/useValidation';
  import { useConfiguratorTracking } from '@/composables/useConfiguratorTracking';
  import { useConfiguratorMigration } from '@/composables/useConfiguratorMigration';
  import ConfiguratorMigrationModal from '@/components/products/ConfiguratorMigrationModal.vue';
  import ProductInstallationRequirements from '@/components/products/ProductInstallationRequirements.vue';
  import {
    getProductFeeds,
    updateProductInFeed,
    addProductToFeed,
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getProductById,
    bulkUpdateProducts,
  } from '@shared/services/productService';
  import { supplierService } from '@/services/supplierService';
  import { clusterAutoFillOptions } from '@shared/utils/autoFill';
  import FileService from '@/services/fileService';
  import {
    basisToPricesIncludeVat,
    pricesIncludeVatToBasis,
    resolveDisplayBasis,
    toDisplayBasisAmount,
  } from '@shared/utils/moneyUtils';
  import { formatHoursInput, parseHoursInput } from '@/utils/standardHours';
  import { productUnitOptions } from '@/utils/productUnit';
  import platformAdminService from '@/services/platformAdminService';

  import Breadcrumb from '@/components/partials/Breadcrumb.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';

  // Input components
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import CurrencyInput from '@/components/inputs/CurrencyInput.vue';
  import MultiSelectInput from '@/components/inputs/MultiSelectInput.vue';
  import FileInput from '@/components/inputs/FileInput.vue';
  import USPInput from '@/components/inputs/USPInput.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import Card from '@/components/partials/Card.vue';
  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  import ProductOptionModal from '@/components/products/ProductOptionModal.vue';
  import CopyProductOptionModal from '@/components/products/CopyProductOptionModal.vue';
  import OptionGroupModal from '@/components/products/OptionGroupModal.vue';
  import UpsellModal from '@/components/products/UpsellModal.vue';
  import BulkPriceUpdateModal from '@/components/products/BulkPriceUpdateModal.vue';
  import CrossOptionRulesEditor from '@/components/products/CrossOptionRulesEditor.vue';
  import VolumePricingRulesEditor from '@/components/products/VolumePricingRulesEditor.vue';
  import ComputedFieldsEditor from '@/components/products/ComputedFieldsEditor.vue';
  import MatrixPricingEditor from '@/components/products/MatrixPricingEditor.vue';
  import AutoIncludeRulesEditor from '@/components/products/AutoIncludeRulesEditor.vue';
  import ChoiceConstraintEditor from '@/components/products/ChoiceConstraintEditor.vue';
  import SupplierMappingTab from '@/components/products/SupplierMappingTab.vue';
  import CatalogAgreementCard from '@/components/products/CatalogAgreementCard.vue';
  import CatalogLockNotice from '@/components/products/CatalogLockNotice.vue';
  import CatalogRulesTab from '@/components/products/CatalogRulesTab.vue';
  import CatalogPriceOverridesCard from '@/components/products/CatalogPriceOverridesCard.vue';
  import CatalogAssortmentCard from '@/components/products/CatalogAssortmentCard.vue';
  import CatalogOwnFieldsCard from '@/components/products/CatalogOwnFieldsCard.vue';
  import ProductConfiguratorForm from '@/components/products/ProductConfiguratorForm.vue';
  import { PRICE_BASIS_LABELS } from '@/components/products/CatalogAgreementFields.vue';
  import Label from '@/components/partials/Label.vue';
  import { catalogService } from '@/services/catalogService';
  import CategoryManagerModal from '@/components/products/CategoryManagerModal.vue';
  import CatalogPublishModal from '@/components/products/CatalogPublishModal.vue';
  import draggable from 'vuedraggable';
  export default defineComponent({
    name: 'ProductDetailPage',

    components: {
      Breadcrumb,
      Btn,
      ProductInstallationRequirements,
      BaseIcon,
      PageHeader,
      TextInput,
      NumberInput,
      TextAreaInput,
      CheckboxInput,
      ToggleInput,
      SelectInput,
      CurrencyInput,
      MultiSelectInput,
      FileInput,
      USPInput,
      EmptyState,
      Card,
      Tabs,
      Tab,
      ProductOptionModal,
      CopyProductOptionModal,
      OptionGroupModal,
      UpsellModal,
      CrossOptionRulesEditor,
      VolumePricingRulesEditor,
      ComputedFieldsEditor,
      MatrixPricingEditor,
      AutoIncludeRulesEditor,
      ChoiceConstraintEditor,
      SupplierMappingTab,
      CatalogAgreementCard,
      CatalogLockNotice,
      CatalogRulesTab,
      CatalogPriceOverridesCard,
      CatalogAssortmentCard,
      CatalogOwnFieldsCard,
      ProductConfiguratorForm,
      Label,
      draggable,
    },

    setup() {
      const router = useRouter();
      const route = useRoute();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const { notifyConfiguratorsOfProductChange, getProductUsageStats } =
        useConfiguratorTracking();
      const { checkMigrationNeeded } = useConfiguratorMigration();

      const toHoursBinding = (product) =>
        formatHoursInput(product?.standardHours ?? product?.standard_hours ?? '');
      const toInstallationRequirements = (product) => {
        const value = product?.installationRequirements ?? product?.installation_requirements;
        return Array.isArray(value) ? [...value] : [];
      };

      const normalizeStandardHoursInput = () => {
        const formatted = formatHoursInput(inputs.value.standardHours.binding);
        inputs.value.standardHours.binding = formatted;
        productForm.standardHours = formatted;
      };

      const feedId = route.params.feedId;
      const productParam = route.params.productIndex; // Can be UUID or 'new' or numeric index
      const isNewProduct = productParam === 'new';
      const isCatalogMaster = route.name === 'PlatformCatalogMaster';
      const catalogPreviewOpen = ref(false);
      const catalogDraft = ref(null);
      const catalogOrderMapping = ref(null);
      const publishingCatalog = ref(false);
      const catalogPriceBasis = ref('brochure_incl_vat');
      const catalogCategory = ref('');
      const catalogCategoryOptions = ref([]);
      const catalogInstallationId = ref(null);
      const catalogPriceBasisOptions = [
        { value: 'brochure_incl_vat', label: 'Brochureprijs incl. btw (consumentenprijs)' },
        { value: 'brochure_excl_vat', label: 'Brochureprijs excl. btw' },
        { value: 'net_purchase_excl_vat', label: 'Netto inkooplijst excl. btw' },
      ];
      const catalogPriceBasisHint = computed(() => {
        if (catalogPriceBasis.value === 'net_purchase_excl_vat') {
          return 'De cel is de inkoop van de dealer. De dealer vult een marge in.';
        }
        if (catalogPriceBasis.value === 'brochure_incl_vat') {
          return 'De cel is het bedrag uit het boekje, inclusief btw. De dealer vult een inkoopkorting in.';
        }
        return 'De cel is de brochureprijs zonder btw. De dealer vult een inkoopkorting in.';
      });

      const feeds = ref([]);
      const currentFeed = ref(null);
      const saving = ref(false);
      const availableCategories = ref([]);
      const suppliers = ref([]);
      const activeTab = ref('basis');

      // Configuration count for tab badge
      const configurationCount = computed(() => {
        const optionsCount = productForm.options?.length || 0;
        const upsellsCount = productForm.upsells?.length || 0;
        return optionsCount + upsellsCount;
      });

      // Category options for MultiSelectInput
      const categoryOptions = computed(() => {
        return availableCategories.value.map((category) => ({
          value: category.id,
          label: category.name,
          color: category.color,
        }));
      });

      const unitOptions = computed(() => productUnitOptions($t));

      // Supplier options for SelectInput
      const supplierOptions = computed(() => {
        return [
          { value: null, label: 'Geen leverancier geselecteerd' },
          ...suppliers.value.map((supplier) => ({
            value: supplier.id,
            label: supplier.name,
          })),
        ];
      });

      // Validation setup
      const { inputs, validateForm, onValidate } = useValidation({
        title: {
          id: 'product_title',
          label: 'Titel',
          binding: '',
          error: '',
          rules: ['required', 'maxLength:255'],
          placeholder: 'Product titel',
        },
        url: {
          id: 'product_url',
          label: 'URL',
          binding: '',
          error: '',
          rules: ['url'],
          placeholder: 'https://...',
        },
        imageUrl: {
          id: 'product_image_url',
          label: 'Afbeelding URL',
          binding: '',
          error: '',
          rules: ['url'],
          placeholder: 'https://...',
        },
        price: {
          id: 'product_price',
          label: 'Prijs',
          binding: 0,
          error: '',
          rules: ['min:0'],
        },
        salePrice: {
          id: 'product_sale_price',
          label: 'Aanbiedingsprijs',
          binding: 0,
          error: '',
          rules: ['min:0'],
        },
        baseSku: {
          id: 'product_base_sku',
          label: 'Basis SKU',
          binding: '',
          error: '',
          rules: ['maxLength:50'],
          placeholder: 'ROL',
        },
        productionNotes: {
          id: 'product_production_notes',
          label: 'Productie Notities',
          binding: '',
          error: '',
          rules: ['maxLength:2000'],
          placeholder: 'Speciale instructies...',
        },
        standardHours: {
          id: 'product_standard_hours',
          label: 'Normuren',
          binding: '',
          error: '',
          rules: ['min:0'],
          placeholder: 'bijv. 1,5',
        },
        proposalNotes: {
          id: 'product_proposal_notes',
          label: 'Proposal Notities',
          binding: '',
          error: '',
          rules: ['maxLength:2000'],
          placeholder: 'Notities voor proposal...',
        },
        description: {
          id: 'product_description',
          label: 'Beschrijving',
          binding: '',
          error: '',
          rules: ['maxLength:2000'],
          placeholder: 'Product beschrijving...',
        },
      });

      const sanitizeOptionPageGroups = (options, optionGroups) => {
        const validGroupIds = new Set((optionGroups || []).map((g) => g.id));
        return options.map((opt) => {
          if (opt.pageGroupId && !validGroupIds.has(opt.pageGroupId)) {
            return { ...opt, pageGroupId: null };
          }
          return opt;
        });
      };

      const normalizePricingMatrixFromApi = (matrix) => {
        if (!matrix || typeof matrix !== 'object') return null;

        const dimensions = matrix.dimensions || {};
        const matrixSteps = matrix.matrix_steps || matrix.matrixSteps || {};
        const matrixStarts = matrix.matrix_starts || matrix.matrixStarts || {};
        const columnValues = matrix.column_values || matrix.columnValues || [];
        const manualColumns = matrix.manual_columns ?? matrix.manualColumns ?? null;
        const grid = Array.isArray(matrix.grid)
          ? matrix.grid.map((row) => ({
              groupValue: row.group_value ?? row.groupValue ?? null,
              rowValue: row.row_value ?? row.rowValue ?? null,
              prices: Array.isArray(row.prices) ? row.prices : [],
            }))
          : [];

        return {
          ...matrix,
          dimensions: {
            row: dimensions.row || '',
            col: dimensions.col || '',
            group: dimensions.group || '',
          },
          matrixSteps: {
            row: matrixSteps.row ?? null,
            col: matrixSteps.col ?? null,
            group: matrixSteps.group ?? null,
          },
          matrixStarts: {
            row: matrixStarts.row ?? null,
            col: matrixStarts.col ?? null,
            group: matrixStarts.group ?? null,
          },
          columnValues: Array.isArray(columnValues) ? columnValues : [],
          manualColumns,
          grid,
        };
      };

      const normalizePricingDeltasFromApi = (tables) => {
        if (!Array.isArray(tables)) return [];

        return tables.map((table) => {
          const dimensions = table?.dimensions || {};
          const matrixSteps = table?.matrix_steps || table?.matrixSteps || {};
          const matrixStarts = table?.matrix_starts || table?.matrixStarts || {};
          const columnValues = table?.column_values || table?.columnValues || [];
          const defaultValue = table?.default_value ?? table?.defaultValue ?? 0;
          const manualColumns = table?.manual_columns ?? table?.manualColumns ?? null;
          const grid = Array.isArray(table?.grid)
            ? table.grid.map((row) => ({
                groupValue: row.group_value ?? row.groupValue ?? null,
                rowValue: row.row_value ?? row.rowValue ?? null,
                values: Array.isArray(row.values) ? row.values : [],
              }))
            : [];

          return {
            ...table,
            defaultValue,
            dimensions: {
              row: dimensions.row || '',
              col: dimensions.col || '',
              group: dimensions.group || '',
            },
            matrixSteps: {
              row: matrixSteps.row ?? null,
              col: matrixSteps.col ?? null,
              group: matrixSteps.group ?? null,
            },
            matrixStarts: {
              row: matrixStarts.row ?? null,
              col: matrixStarts.col ?? null,
              group: matrixStarts.group ?? null,
            },
            columnValues: Array.isArray(columnValues) ? columnValues : [],
            manualColumns,
            grid,
          };
        });
      };

      // Force a prices/values row to exactly `count` entries: truncate trailing
      // overflow columns and pad missing ones with 0. Guarantees the backend
      // invariant `grid[i].prices.length === column_values.length`, even for legacy
      // data where the column dimension was shrunk after the grid was generated.
      const clampRowToColumnCount = (values, count) => {
        const arr = Array.isArray(values) ? values.slice(0, count) : [];
        while (arr.length < count) arr.push(0);
        return arr;
      };

      const serializePricingMatrixToApi = (matrix) => {
        if (!matrix || typeof matrix !== 'object') return null;

        const columnValues = Array.isArray(matrix?.columnValues) ? matrix.columnValues : [];
        const columnCount = columnValues.length;

        return {
          dimensions: {
            row: matrix?.dimensions?.row || '',
            col: matrix?.dimensions?.col || '',
            group: matrix?.dimensions?.group || null,
          },
          matrix_steps: {
            row: matrix?.matrixSteps?.row ?? null,
            col: matrix?.matrixSteps?.col ?? null,
            group: matrix?.matrixSteps?.group ?? null,
          },
          matrix_starts: {
            row: matrix?.matrixStarts?.row ?? null,
            col: matrix?.matrixStarts?.col ?? null,
            group: matrix?.matrixStarts?.group ?? null,
          },
          column_values: columnValues,
          grid: Array.isArray(matrix?.grid)
            ? matrix.grid.map((row) => ({
                group_value: row?.groupValue ?? null,
                row_value: row?.rowValue ?? null,
                prices: clampRowToColumnCount(row?.prices, columnCount),
              }))
            : [],
        };
      };

      const serializePricingDeltasToApi = (tables) => {
        if (!Array.isArray(tables)) return [];

        return tables.map((table) => {
          const columnValues = Array.isArray(table?.columnValues) ? table.columnValues : [];
          // A delta without a column dimension still stores a single value per row,
          // so the effective column count is at least 1.
          const columnCount = columnValues.length || 1;

          return {
            id: table?.id,
            label: table?.label || '',
            active: table?.active !== false,
            default_value: Number(table?.defaultValue || 0),
            dimensions: {
              row: table?.dimensions?.row || '',
              col: table?.dimensions?.col || '',
              group: table?.dimensions?.group || null,
            },
            matrix_steps: {
              row: table?.matrixSteps?.row ?? null,
              col: table?.matrixSteps?.col ?? null,
              group: table?.matrixSteps?.group ?? null,
            },
            matrix_starts: {
              row: table?.matrixStarts?.row ?? null,
              col: table?.matrixStarts?.col ?? null,
              group: table?.matrixStarts?.group ?? null,
            },
            column_values: columnValues,
            grid: Array.isArray(table?.grid)
              ? table.grid.map((row) => ({
                  group_value: row?.groupValue ?? null,
                  row_value: row?.rowValue ?? null,
                  values: clampRowToColumnCount(row?.values, columnCount),
                }))
              : [],
          };
        });
      };

      // Product form state
      const productForm = reactive({
        title: '',
        url: '',
        imageUrl: '',
        price: null,
        salePrice: null,
        baseSku: '',
        standardHours: '',
        installationRequirements: [],
        productionNotes: '',
        proposalNotes: '',
        proposalAttachments: [],
        label: '',
        position: null,
        description: '',
        category: '',
        usps: [],
        upsells: [],
        upsellGroups: [], // Array of {id: string, name: string} for upsell group ordering
        options: [],
        optionGroups: [], // Array of {id: string, name: string} for option group ordering
        crossOptionRules: [],
        volumePricingRules: [],
        computedFields: [], // Array of {id, label, formula} for computed values
        autoIncludeRules: [],
        attributeValues: {},
        categoryIds: [],
        unit: null,
        useMatrixPricing: false,
        pricingMatrix: null,
        pricingDeltas: [],
        choiceConstraints: [],
        supplierId: null,
        // Margin & VAT (Phase 2, 2026-05-12) — direct columns on `products`.
        // Defaults match the backend: target = 0 (no margin transformation),
        // min = 0 (no threshold), vat_rate = 21, prices_include_vat = null
        // (follow organisation default).
        targetMarginPct: 0,
        minMarginPct: 0,
        vatRate: 21,
        displayBasis: 'incl',
        pricesIncludeVat: null,
      });

      // Staffelbasis-opties voor volume pricing: numerieke opties en berekende
      // velden van dit product (bijv. oppervlakte = lengte × breedte).
      const volumeBasisFieldOptions = computed(() => {
        const numberOptions = (productForm.options || [])
          .filter((option) => option?.type === 'number' && option?.id)
          .map((option) => ({
            value: option.id,
            label: option.label || option.id,
            unit: option.numberConfig?.unit || '',
          }));

        const computedFields = (productForm.computedFields || [])
          .filter((field) => field?.id && field?.formula)
          .map((field) => ({
            value: field.id,
            label: field.label || field.id,
            unit: field.unit || '',
          }));

        return [...numberOptions, ...computedFields];
      });

      // Margin & VAT UI helpers
      const showAdvancedVat = ref(false);
      const vatRateOptions = [
        { value: 0, label: '0% (vrijgesteld / nultarief)' },
        { value: 9, label: '9% (laag tarief)' },
        { value: 21, label: '21% (hoog tarief)' },
      ];
      const vatBasisOptions = [
        {
          value: null,
          label: 'Volg organisatie',
          hint: 'Gebruik de instelling uit Settings → Organisatie (standaard).',
        },
        {
          value: true,
          label: 'Inclusief BTW',
          hint: 'De ingevoerde prijs is altijd incl. BTW, ongeacht org-instelling.',
        },
        {
          value: false,
          label: 'Exclusief BTW',
          hint: 'De ingevoerde prijs is altijd excl. BTW, ongeacht org-instelling.',
        },
      ];
      const productDisplayBasisOptions = [
        { value: 'incl', label: 'Incl. BTW' },
        { value: 'excl', label: 'Excl. BTW' },
      ];
      const productPriceLabel = computed(() =>
        productForm.displayBasis === 'incl' ? 'Prijs incl. BTW' : 'Prijs excl. BTW'
      );
      const productSalePriceLabel = computed(() =>
        productForm.displayBasis === 'incl'
          ? 'Aanbiedingsprijs incl. BTW'
          : 'Aanbiedingsprijs excl. BTW'
      );

      const convertPriceValue = (value, fromBasis, toBasis) => {
        if (value == null || value === '') return value;
        return toDisplayBasisAmount(value, productForm.vatRate, fromBasis, toBasis);
      };

      const convertObjectPriceFields = (target, fields, fromBasis, toBasis) => {
        if (!target || typeof target !== 'object') return;
        fields.forEach((field) => {
          if (target[field] != null) {
            target[field] = convertPriceValue(target[field], fromBasis, toBasis);
          }
        });
      };

      const convertProductPriceFields = (fromBasis, toBasis) => {
        inputs.value.price.binding = convertPriceValue(
          inputs.value.price.binding,
          fromBasis,
          toBasis
        );
        inputs.value.salePrice.binding = convertPriceValue(
          inputs.value.salePrice.binding,
          fromBasis,
          toBasis
        );

        (productForm.pricingMatrix?.grid || []).forEach((row) => {
          if (Array.isArray(row.prices)) {
            row.prices = row.prices.map((price) => convertPriceValue(price, fromBasis, toBasis));
          }
        });

        (productForm.pricingDeltas || []).forEach((table) => {
          if (table?.defaultValue != null) {
            table.defaultValue = convertPriceValue(table.defaultValue, fromBasis, toBasis);
          }
          (table?.grid || []).forEach((row) => {
            if (Array.isArray(row.values)) {
              row.values = row.values.map((value) => convertPriceValue(value, fromBasis, toBasis));
            }
          });
          (table?.cells || []).forEach((cell) => {
            if (cell?.value != null) {
              cell.value = convertPriceValue(cell.value, fromBasis, toBasis);
            }
          });
        });

        (productForm.options || []).forEach((option) => {
          convertObjectPriceFields(
            option.numberConfig,
            ['basePrice', 'pricePerUnit', 'fixedPrice'],
            fromBasis,
            toBasis
          );
          convertObjectPriceFields(
            option.numberConfig?.matrixConfig,
            ['pricePerUnit', 'minimumPrice'],
            fromBasis,
            toBasis
          );
          (option.choiceConfig?.choices || []).forEach((choice) => {
            convertObjectPriceFields(choice, ['price'], fromBasis, toBasis);
          });
        });

        (productForm.upsells || []).forEach((upsell) => {
          convertObjectPriceFields(upsell, ['price'], fromBasis, toBasis);
        });

        (productForm.crossOptionRules || []).forEach((rule) => {
          (rule.actions || []).forEach((action) => {
            if (['add', 'subtract'].includes(action.type)) {
              convertObjectPriceFields(action, ['value'], fromBasis, toBasis);
            }
          });
        });

        (productForm.volumePricingRules || []).forEach((rule) => {
          if (['add', 'subtract', 'override'].includes(rule.action?.type)) {
            convertObjectPriceFields(rule.action, ['value'], fromBasis, toBasis);
          }
          (rule.tiers || []).forEach((tier) => {
            if (['add', 'subtract', 'override'].includes(tier.action?.type)) {
              convertObjectPriceFields(tier.action, ['value'], fromBasis, toBasis);
            }
          });
        });

        (productForm.autoIncludeRules || []).forEach((rule) => {
          convertObjectPriceFields(rule, ['price'], fromBasis, toBasis);
        });
      };

      const handleProductDisplayBasisChange = (basis) => {
        const previousBasis = productForm.displayBasis || 'incl';
        if (previousBasis === basis) return;
        convertProductPriceFields(previousBasis, basis);
        productForm.displayBasis = basis;
        productForm.pricesIncludeVat = basisToPricesIncludeVat(basis);
      };

      const handleLegacyProductBasisChange = (pricesIncludeVat) => {
        const basis =
          pricesIncludeVat === null
            ? resolveDisplayBasis({}, userStore.currentOrganization)
            : pricesIncludeVatToBasis(pricesIncludeVat);
        handleProductDisplayBasisChange(basis);
      };

      // Live preview of the projected selling price (cost / (1 - m/100)) so
      // the user sees the effect of the margin field immediately. Hidden when
      // margin is 0 (then the price is taken as-is, no transformation).
      const marginPreview = computed(() => {
        const cost = Number(inputs.value?.price?.binding) || 0;
        const margin = Number(productForm.targetMarginPct) || 0;
        if (!cost || !margin || margin <= 0 || margin >= 100) return '';
        const sell = cost / (1 - margin / 100);
        const fmt = (n) =>
          new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(n);
        return `Voorbeeld: bij ${fmt(cost)} inkoop → ${fmt(sell)} verkoop.`;
      });

      const minMarginAboveTarget = computed(() => {
        const target = Number(productForm.targetMarginPct) || 0;
        const min = Number(productForm.minMarginPct) || 0;
        return target > 0 && min > 0 && min > target;
      });

      // Product option modal state (now handled by uiStore)
      const editingProductOptionIndex = ref(null);

      // Available products for upsell selection (all products from all feeds)
      const availableProductsForUpsells = ref([]);

      // Normalize upsells: convert old format (string[]) to new format (ProductUpsell[])
      const normalizeUpsells = (upsells) => {
        if (!Array.isArray(upsells)) return [];

        const normalized = upsells.map((upsell, index) => {
          // Already new format
          if (typeof upsell === 'object' && upsell.id) {
            return upsell;
          }

          // Old format: string ID
          if (typeof upsell === 'string') {
            // Try to find product in availableProductsForUpsells to get metadata
            const product = availableProductsForUpsells.value.find((p) => p.id === upsell);
            const normalizedUpsell = {
              id: upsell,
              productId: upsell,
              showOnQuote: true,
              showOnPackingSlip: false,
              allowQuantity: false,
              // Add cached metadata if available
              name: product?.name || product?.title,
              price: product?.price,
              imageUrl: product?.imageUrl || product?.image_url,
            };
            console.log(`  🔄 Upsell ${index + 1} converted from string:`, normalizedUpsell);
            return normalizedUpsell;
          }

          return upsell;
        });

        return normalized;
      };

      // Reconstruct upsellGroups from upsells' groupId values if not provided by backend
      const reconstructUpsellGroups = (upsells, existingGroups) => {
        // If we already have groups, use them
        if (existingGroups && existingGroups.length > 0) {
          return existingGroups;
        }

        // Extract unique groupIds from upsells
        const groupIds = new Set();
        upsells?.forEach((upsell) => {
          if (upsell.groupId && upsell.groupId !== null) {
            groupIds.add(upsell.groupId);
          }
        });

        // Create groups with default names
        const reconstructedGroups = [];
        let groupNumber = 1;
        groupIds.forEach((groupId) => {
          reconstructedGroups.push({
            id: groupId,
            name: `Upsell Groep ${groupNumber}`,
          });
          groupNumber++;
        });

        if (reconstructedGroups.length > 0) {
          console.log('📦 Reconstructed upsellGroups from upsells:', reconstructedGroups);
        }

        return reconstructedGroups;
      };

      const productId = computed(() => productForm.id || null);

      const selectedSupplierName = computed(() => {
        if (!productForm.supplierId) return '';
        const s = suppliers.value.find((sup) => sup.id === productForm.supplierId);
        return s?.name || '';
      });

      const catalogSupplierName = computed(
        () => catalogOrderMapping.value?.supplier_name || 'By Michèl'
      );
      // Value sets a master option can bind to. Empty for ordinary products.
      const catalogChoiceSets = ref([]);
      const loadCatalogChoiceSets = async (catalogSupplierId) => {
        try {
          const result = await platformAdminService.getCatalogSets(catalogSupplierId);
          catalogChoiceSets.value = result.sets || [];
        } catch (error) {
          console.error('Error loading catalog sets:', error);
          catalogChoiceSets.value = [];
        }
      };

      // Check if product has ID (even if route says 'new', after creation it should have ID)
      const actualIsNewProduct = computed(() => {
        return !productForm.id && productParam === 'new';
      });

      const breadcrumbs = computed(() => {
        if (isCatalogMaster) {
          return [
            { label: 'Catalogus', url: '/platform/catalog', icon: 'cubes' },
            { label: inputs.value.title.binding || productForm.title || 'Masterproduct' },
          ];
        }
        return [
          {
            label: 'Product feeds',
            url: '/products',
            icon: 'box',
          },
          {
            label: currentFeed.value?.name || 'Feed',
            url: `/products/${feedId}`,
          },
          {
            label: actualIsNewProduct.value
              ? 'Nieuw product'
              : inputs.value.title.binding || productForm.title || 'Product',
          },
        ];
      });

      // Load data
      const applyLoadedProduct = (product) => {
        productForm.id = product.id;
        productForm.title = product.title || '';
        productForm.url = product.url || '';
        productForm.imageUrl = product.imageUrl || product.image_url || '';
        productForm.price = product.price || null;
        productForm.salePrice = product.salePrice || null;
        productForm.baseSku = product.baseSku || product.base_sku || '';
        productForm.standardHours = toHoursBinding(product);
        productForm.unit = product.unit ?? null;
        productForm.installationRequirements = toInstallationRequirements(product);
        productForm.productionNotes = product.productionNotes || product.production_notes || '';
        productForm.proposalNotes = product.proposalNotes || product.proposal_notes || '';
        productForm.proposalAttachments = Array.isArray(
          product.proposalAttachments || product.proposal_attachments
        )
          ? [...(product.proposalAttachments || product.proposal_attachments)]
          : [];
        productForm.description = product.description || '';
        productForm.label = product.label || '';
        productForm.position = product.position || null;
        productForm.category = product.category || '';
        productForm.usps = product.usps || [];
        productForm.upsells = normalizeUpsells(product.upsells || []);
        productForm.upsellGroups = reconstructUpsellGroups(
          productForm.upsells,
          product.upsellGroups || product.upsell_groups
        );
        productForm.optionGroups = product.optionGroups || product.option_groups || [];
        productForm.options = sanitizeOptionPageGroups(
          product.options || [],
          productForm.optionGroups
        );
        productForm.crossOptionRules = product.crossOptionRules || product.cross_option_rules || [];
        productForm.volumePricingRules =
          product.volumePricingRules || product.volume_pricing_rules || [];
        productForm.computedFields = product.computedFields || product.computed_fields || [];
        productForm.autoIncludeRules = product.autoIncludeRules || product.auto_include_rules || [];
        productForm.attributeValues = product.attributeValues || product.attribute_values || {};
        catalogInstallationId.value = isCatalogMaster
          ? null
          : product.catalog_installation_id || product.catalogInstallationId || null;
        productForm.categoryIds = product.categoryIds || [];
        productForm.useMatrixPricing =
          product.useMatrixPricing || product.use_matrix_pricing || false;
        productForm.pricingMatrix = normalizePricingMatrixFromApi(
          product.pricing_matrix || product.pricingMatrix || null
        );
        productForm.pricingDeltas = normalizePricingDeltasFromApi(
          product.pricing_deltas || product.pricingDeltas || []
        );
        productForm.choiceConstraints =
          product.choiceConstraints || product.choice_constraints || [];
        productForm.supplierId = product.supplierId || product.supplier_id || null;
        productForm.targetMarginPct = product.target_margin_pct ?? product.targetMarginPct ?? 0;
        productForm.minMarginPct = product.min_margin_pct ?? product.minMarginPct ?? 0;
        productForm.vatRate = product.vat_rate ?? product.vatRate ?? 21;
        productForm.displayBasis = resolveDisplayBasis(product, userStore.currentOrganization);
        productForm.pricesIncludeVat = basisToPricesIncludeVat(productForm.displayBasis);

        productForm.optionGroups.forEach((g) => {
          groupExpanded.value[g.id] = true;
        });
        productForm.upsellGroups.forEach((g) => {
          upsellGroupExpanded.value[g.id] = true;
        });

        inputs.value.title.binding = product.title || '';
        inputs.value.url.binding = product.url || '';
        inputs.value.imageUrl.binding = product.imageUrl || product.image_url || '';
        inputs.value.price.binding = product.price || 0;
        inputs.value.salePrice.binding = product.salePrice || 0;
        inputs.value.baseSku.binding = product.baseSku || product.base_sku || '';
        inputs.value.standardHours.binding = toHoursBinding(product);
        inputs.value.productionNotes.binding =
          product.productionNotes || product.production_notes || '';
        inputs.value.proposalNotes.binding = product.proposalNotes || product.proposal_notes || '';
        inputs.value.description.binding = product.description || '';

        if (product.feedId && !currentFeed.value) {
          currentFeed.value = feeds.value.find((f) => f.id === product.feedId);
        }
      };

      const catalogVersions = ref([]);
      const formatCatalogDate = (value) =>
        new Date(value).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
      const catalogMasterDescription = computed(() => {
        const base =
          'Zelfde beheer als een product. Opslaan bewaart het concept, ' +
          'publiceren zet een nieuwe versie klaar.';
        const versions = catalogVersions.value;
        const active = versions.find((v) => v.adopted_at);
        const planned = versions.find((v) => !v.adopted_at && !v.superseded_at && v.effective_from);
        const parts = [];
        if (active) parts.push(`Actief: v${active.version_number}`);
        if (planned) {
          const from = formatCatalogDate(planned.effective_from);
          parts.push(`Gepland: v${planned.version_number} vanaf ${from}`);
        }
        return parts.length ? `${base} ${parts.join(' · ')}.` : base;
      });

      const loadCatalogCategoryOptions = async () => {
        try {
          const { masters } = await platformAdminService.getCatalogMasters();
          catalogCategoryOptions.value = [
            ...new Set((masters || []).map((master) => master.category).filter(Boolean)),
          ].sort((a, b) => a.localeCompare(b, 'nl'));
        } catch {
          catalogCategoryOptions.value = [];
        }
      };

      const loadCatalogUpsellCandidates = async () => {
        try {
          const { masters } = await platformAdminService.getCatalogUpsellCandidates();
          availableProductsForUpsells.value = (masters || []).map((master) => ({
            id: master.id,
            title: master.title,
            name: master.name || master.title,
            price: master.price,
            imageUrl: master.image_url,
            image_url: master.image_url,
            description: master.description,
          }));
        } catch {
          availableProductsForUpsells.value = [];
        }
      };

      const loadCatalogMaster = async () => {
        const master = await platformAdminService.getCatalogMaster(route.params.masterId);
        catalogVersions.value = master.versions || [];
        catalogDraft.value = master.draft || {};
        catalogOrderMapping.value = master.draft?.order_mapping || null;
        catalogPriceBasis.value = master.draft?.price_basis || 'brochure_incl_vat';
        catalogCategory.value = master.category || master.draft?.category || '';
        loadCatalogCategoryOptions();
        loadCatalogUpsellCandidates();
        await loadCatalogChoiceSets(master.catalog_supplier_id);
        await nextTick();
        applyLoadedProduct({
          ...catalogDraft.value,
          id: master.id,
          title: catalogDraft.value.title || master.title,
        });
      };

      onMounted(async () => {
        if (isCatalogMaster) {
          try {
            await loadCatalogMaster();
          } catch (error) {
            console.error('Error loading catalog master:', error);
            toastStore.addToast({
              type: 'error',
              message: 'Kon het masterproduct niet laden',
            });
          }
          return;
        }

        try {
          const organizationId = userStore.currentOrganizationId;
          if (!organizationId) {
            console.error('No organization ID available');
            return;
          }

          // Load feeds, categories, and suppliers in parallel
          let feedsData, categoriesData, suppliersData;
          try {
            [feedsData, categoriesData, suppliersData] = await Promise.all([
              getProductFeeds(organizationId),
              getCategories(organizationId),
              supplierService.getAllSuppliers({ limit: 100 }).catch(() => ({ suppliers: [] })),
            ]);
          } catch (error) {
            feedsData = await getProductFeeds(); // Fallback zonder organizationId
            categoriesData = [];
            suppliersData = { suppliers: [] };
          }

          feeds.value = feedsData;
          availableCategories.value = categoriesData;
          suppliers.value = suppliersData.suppliers || suppliersData.data || [];
          currentFeed.value = feeds.value.find((f) => f.id === feedId);
          if (isNewProduct) {
            productForm.displayBasis = resolveDisplayBasis({}, userStore.currentOrganization);
            productForm.pricesIncludeVat = basisToPricesIncludeVat(productForm.displayBasis);
          }

          // Load all products from all feeds for upsell selection
          availableProductsForUpsells.value = feeds.value.flatMap((feed) =>
            (feed.products || []).map((product) => ({
              ...product,
              name: product.name || product.title,
            }))
          );

          if (!isNewProduct && productParam && productParam !== 'new') {
            try {
              // Direct product ophalen via API voor volledige data
              const product = await getProductById(productParam);

              await nextTick();
              applyLoadedProduct(product);
            } catch (error) {
              console.error('Error loading product by ID, falling back to feed:', error);
              // The feed list carries a stripped copy (no groups, rules or matrix). Saving
              // from that copy would wipe them, so make the failure visible.
              toastStore.addToast({
                type: 'error',
                message:
                  'Product kon niet volledig geladen worden. Herlaad de pagina voordat je opslaat.',
                duration: 8000,
              });
              // Fallback: probeer via feed (voor backwards compatibility)
              if (currentFeed.value) {
                let product = null;

                // Try to find product by UUID first
                product = currentFeed.value.products.find((p) => p.id === productParam);

                // If not found by UUID, try by index (for backwards compatibility)
                if (!product && !isNaN(parseInt(productParam))) {
                  product = currentFeed.value.products[parseInt(productParam)];
                }

                if (product) {
                  // Wait for next tick to ensure all components are mounted
                  await nextTick();

                  // Load into productForm for non-validated fields
                  // Use individual assignments to avoid triggering all watchers at once
                  productForm.id = product.id;
                  productForm.title = product.title || '';
                  productForm.url = product.url || '';
                  productForm.imageUrl = product.imageUrl || '';
                  productForm.price = product.price || null;
                  productForm.salePrice = product.salePrice || null;
                  productForm.baseSku = product.baseSku || product.base_sku || '';
                  productForm.standardHours = toHoursBinding(product);
                  productForm.unit = product.unit ?? null;
                  productForm.installationRequirements = toInstallationRequirements(product);
                  productForm.productionNotes =
                    product.productionNotes || product.production_notes || '';
                  productForm.proposalNotes = product.proposalNotes || product.proposal_notes || '';
                  productForm.proposalAttachments = Array.isArray(
                    product.proposalAttachments || product.proposal_attachments
                  )
                    ? [...(product.proposalAttachments || product.proposal_attachments)]
                    : [];
                  productForm.description = product.description || '';
                  productForm.label = product.label || '';
                  productForm.position = product.position || null;
                  productForm.category = product.category || '';
                  productForm.usps = product.usps || [];
                  // Normalize upsells: convert old format (string[]) to new format (ProductUpsell[])
                  productForm.upsells = normalizeUpsells(product.upsells || []);
                  // Reconstruct upsellGroups from upsells' groupId values if not provided by backend
                  productForm.upsellGroups = reconstructUpsellGroups(
                    productForm.upsells,
                    product.upsellGroups
                  );
                  productForm.optionGroups = product.optionGroups || [];
                  productForm.options = sanitizeOptionPageGroups(
                    product.options || [],
                    productForm.optionGroups
                  );
                  productForm.crossOptionRules = product.crossOptionRules || [];
                  productForm.volumePricingRules =
                    product.volumePricingRules || product.volume_pricing_rules || [];
                  productForm.computedFields =
                    product.computedFields || product.computed_fields || [];
                  productForm.autoIncludeRules =
                    product.autoIncludeRules || product.auto_include_rules || [];
                  productForm.attributeValues = product.attributeValues || {};
                  productForm.categoryIds = product.categoryIds || [];
                  productForm.useMatrixPricing =
                    product.useMatrixPricing || product.use_matrix_pricing || false;
                  productForm.pricingMatrix = normalizePricingMatrixFromApi(
                    product.pricing_matrix || product.pricingMatrix || null
                  );
                  productForm.pricingDeltas = normalizePricingDeltasFromApi(
                    product.pricing_deltas || product.pricingDeltas || []
                  );
                  productForm.choiceConstraints =
                    product.choiceConstraints || product.choice_constraints || [];
                  // Margin & VAT (Phase 2) — same load logic as the primary
                  // path above. Keeping both in sync so legacy feed-based
                  // lookups don't lose the new fields.
                  productForm.targetMarginPct =
                    product.target_margin_pct ?? product.targetMarginPct ?? 0;
                  productForm.minMarginPct = product.min_margin_pct ?? product.minMarginPct ?? 0;
                  productForm.vatRate = product.vat_rate ?? product.vatRate ?? 21;
                  productForm.displayBasis = resolveDisplayBasis(
                    product,
                    userStore.currentOrganization
                  );
                  productForm.pricesIncludeVat = basisToPricesIncludeVat(productForm.displayBasis);

                  // Initialize expanded state for all groups
                  productForm.optionGroups.forEach((g) => {
                    groupExpanded.value[g.id] = true;
                  });

                  // Initialize expanded state for upsell groups
                  productForm.upsellGroups.forEach((g) => {
                    upsellGroupExpanded.value[g.id] = true;
                  });

                  // Load into inputs for validated fields
                  inputs.value.title.binding = product.title || '';
                  inputs.value.url.binding = product.url || '';
                  inputs.value.imageUrl.binding = product.imageUrl || '';
                  inputs.value.price.binding = product.price || 0;
                  inputs.value.salePrice.binding = product.salePrice || 0;
                  inputs.value.baseSku.binding = product.baseSku || product.base_sku || '';
                  inputs.value.standardHours.binding = toHoursBinding(product);
                  inputs.value.productionNotes.binding =
                    product.productionNotes || product.production_notes || '';
                  inputs.value.proposalNotes.binding =
                    product.proposalNotes || product.proposal_notes || '';
                  inputs.value.description.binding = product.description || '';
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading product data:', error);
        } finally {
          // Arm the auto-fill toast only after the load-triggered self-heal has
          // run, so healing legacy stale flags on open doesn't fire a toast.
          // From here on, a flag flipping off is a real user edit worth noting.
          await nextTick();
          autoFillHealArmed.value = true;
        }
      });

      const loadProductIntoForm = async (product) => {
        await nextTick();

        // Programmatic reload (e.g. after a price refresh) — suppress the
        // auto-fill toast while options are replaced. try/finally guarantees the
        // re-arm runs even if an assignment throws, so a failed reload can't
        // leave the toast muted for the rest of the session.
        autoFillHealArmed.value = false;
        try {
          productForm.id = product.id;
          productForm.title = product.title || '';
          productForm.url = product.url || '';
          productForm.imageUrl = product.imageUrl || '';
          productForm.price = product.price || null;
          productForm.salePrice = product.salePrice || null;
          productForm.baseSku = product.baseSku || product.base_sku || '';
          productForm.standardHours = toHoursBinding(product);
          productForm.unit = product.unit ?? null;
          productForm.installationRequirements = toInstallationRequirements(product);
          productForm.productionNotes = product.productionNotes || product.production_notes || '';
          productForm.proposalNotes = product.proposalNotes || product.proposal_notes || '';
          productForm.proposalAttachments = Array.isArray(
            product.proposalAttachments || product.proposal_attachments
          )
            ? [...(product.proposalAttachments || product.proposal_attachments)]
            : [];
          productForm.description = product.description || '';
          productForm.label = product.label || '';
          productForm.position = product.position || null;
          productForm.category = product.category || '';
          productForm.usps = product.usps || [];
          productForm.upsells = normalizeUpsells(product.upsells || []);
          productForm.upsellGroups = reconstructUpsellGroups(
            productForm.upsells,
            product.upsellGroups
          );
          productForm.optionGroups = product.optionGroups || [];
          productForm.options = sanitizeOptionPageGroups(
            product.options || [],
            productForm.optionGroups
          );
          productForm.crossOptionRules = product.crossOptionRules || [];
          productForm.volumePricingRules =
            product.volumePricingRules || product.volume_pricing_rules || [];
          productForm.computedFields = product.computedFields || product.computed_fields || [];
          productForm.autoIncludeRules =
            product.autoIncludeRules || product.auto_include_rules || [];
          productForm.attributeValues = product.attributeValues || {};
          productForm.categoryIds = product.categoryIds || [];
          productForm.useMatrixPricing =
            product.useMatrixPricing || product.use_matrix_pricing || false;
          productForm.pricingMatrix = normalizePricingMatrixFromApi(
            product.pricing_matrix || product.pricingMatrix || null
          );
          productForm.pricingDeltas = normalizePricingDeltasFromApi(
            product.pricing_deltas || product.pricingDeltas || []
          );
          productForm.choiceConstraints =
            product.choiceConstraints || product.choice_constraints || [];
          productForm.supplierId = product.supplierId || product.supplier_id || null;
          productForm.targetMarginPct = product.target_margin_pct ?? product.targetMarginPct ?? 0;
          productForm.minMarginPct = product.min_margin_pct ?? product.minMarginPct ?? 0;
          productForm.vatRate = product.vat_rate ?? product.vatRate ?? 21;
          productForm.displayBasis = resolveDisplayBasis(product, userStore.currentOrganization);
          productForm.pricesIncludeVat = basisToPricesIncludeVat(productForm.displayBasis);

          groupExpanded.value = {};
          productForm.optionGroups.forEach((g) => {
            groupExpanded.value[g.id] = true;
          });

          upsellGroupExpanded.value = {};
          productForm.upsellGroups.forEach((g) => {
            upsellGroupExpanded.value[g.id] = true;
          });

          inputs.value.title.binding = product.title || '';
          inputs.value.url.binding = product.url || '';
          inputs.value.imageUrl.binding = product.imageUrl || '';
          inputs.value.price.binding = product.price || 0;
          inputs.value.salePrice.binding = product.salePrice || 0;
          inputs.value.baseSku.binding = product.baseSku || product.base_sku || '';
          inputs.value.standardHours.binding = toHoursBinding(product);
          inputs.value.productionNotes.binding =
            product.productionNotes || product.production_notes || '';
          inputs.value.proposalNotes.binding =
            product.proposalNotes || product.proposal_notes || '';
          inputs.value.description.binding = product.description || '';

          if (product.feedId && !currentFeed.value) {
            currentFeed.value = feeds.value.find((f) => f.id === product.feedId);
          }
        } finally {
          await nextTick();
          autoFillHealArmed.value = true;
        }
      };

      // Methods
      const goBack = () => {
        router.push('/product-feeds');
      };

      const checkAndHandleMigrations = async (productId) => {
        try {
          // Get configurators using this product
          const configuratorIds = await getProductUsageStats(productId);

          if (!configuratorIds || !Array.isArray(configuratorIds) || configuratorIds.length === 0) {
            return; // No configurators use this product
          }

          // Check each configurator for migration needs
          for (const configuratorId of configuratorIds) {
            const migrationCheck = await checkMigrationNeeded(configuratorId);

            if (migrationCheck.needed) {
              // Show migration modal
              const modalId = uiStore.addModal(ConfiguratorMigrationModal, {
                title: 'Configurator Migratie',
                description: 'Product wijzigingen gedetecteerd',
                migrationData: migrationCheck,
                onMigrate: async (result) => {
                  if (result.success) {
                    console.log('Migration completed successfully:', result);
                  } else {
                    console.error('Migration failed:', result.error);
                    alert(`Migratie gefaald: ${result.error}`);
                  }
                  uiStore.closeModal(modalId);
                },
                onIgnore: () => {
                  console.log('Migration ignored for configurator:', configuratorId);
                  uiStore.closeModal(modalId);
                },
                onCancel: () => {
                  uiStore.closeModal(modalId);
                },
              });

              // Only show one migration modal at a time
              break;
            }
          }
        } catch (error) {
          console.error('Failed to check migrations:', error);
        }
      };

      // --- Product default proposal attachments ---
      const proposalAttachmentInputRef = ref(null);
      const isProposalAttachmentUploading = ref(false);
      const MAX_PROPOSAL_ATTACHMENT_SIZE = 5 * 1024 * 1024;

      const triggerProposalAttachmentUpload = () => {
        proposalAttachmentInputRef.value?.click();
      };

      const handleProposalAttachmentUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        isProposalAttachmentUploading.value = true;
        let uploadedCount = 0;
        try {
          for (const file of files) {
            const isPdf =
              file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
              toastStore.addToast({
                message: 'Alleen PDF-bestanden zijn toegestaan',
                type: 'error',
              });
              continue;
            }
            if (file.size > MAX_PROPOSAL_ATTACHMENT_SIZE) {
              toastStore.addToast({
                message: `${file.name} is te groot (maximaal 5MB)`,
                type: 'error',
              });
              continue;
            }

            const uploaded = await FileService.uploadFile(file, {
              type: 'proposal_attachment',
              organizationId: userStore.currentOrganizationId,
              context: 'product',
              productId: productForm.id || 'new',
            });

            productForm.proposalAttachments.push({
              id: uploaded.id,
              name: uploaded.original_name || file.name,
              url: uploaded.url,
              file_name: uploaded.file_name,
              size: uploaded.size ?? file.size,
            });
            uploadedCount += 1;
          }

          if (uploadedCount > 0) {
            toastStore.addToast({
              message:
                uploadedCount === 1
                  ? 'Bijlage toegevoegd — vergeet niet op te slaan'
                  : `${uploadedCount} bijlagen toegevoegd — vergeet niet op te slaan`,
              type: 'success',
            });
          }
        } catch (error) {
          console.error('Failed to upload product proposal attachment:', error);
          toastStore.addToast({
            message: 'Uploaden van bijlage mislukt',
            type: 'error',
          });
        } finally {
          isProposalAttachmentUploading.value = false;
        }
      };

      const removeProposalAttachment = (index) => {
        productForm.proposalAttachments.splice(index, 1);
      };

      const saveProduct = async ({ silent = false } = {}) => {
        try {
          saving.value = true;

          if (catalogInstallationId.value && matrixEditing.value) {
            await saveMatrixOverride();
          }

          // Validate form first
          const isValid = await validateForm();
          if (!isValid) {
            alert('Vul alle verplichte velden correct in');
            return false;
          }

          // Combine inputs with productForm data
          const normalizedPricingMatrix = productForm.pricingMatrix
            ? { ...productForm.pricingMatrix }
            : null;
          if (normalizedPricingMatrix && normalizedPricingMatrix.deltaTables) {
            delete normalizedPricingMatrix.deltaTables;
          }
          const serializedPricingMatrix = serializePricingMatrixToApi(normalizedPricingMatrix);
          const serializedPricingDeltas = serializePricingDeltasToApi(productForm.pricingDeltas);

          const productData = {
            ...productForm,
            title: inputs.value.title.binding,
            url: inputs.value.url.binding,
            imageUrl: inputs.value.imageUrl.binding,
            price: inputs.value.price.binding,
            salePrice: inputs.value.salePrice.binding,
            baseSku: inputs.value.baseSku.binding,
            standardHours: parseHoursInput(inputs.value.standardHours.binding),
            productionNotes: inputs.value.productionNotes.binding,
            proposalNotes: inputs.value.proposalNotes.binding,
            proposalAttachments: productForm.proposalAttachments,
            description: inputs.value.description.binding,
            supplierId: productForm.supplierId,
            // Ensure margin is sent as a numeric camelCase field.
            targetMarginPct: Number(productForm.targetMarginPct) || 0,
            min_margin_pct: Number(productForm.minMarginPct) || 0,
            vat_rate: Number(productForm.vatRate) || 21,
            display_basis: productForm.displayBasis,
            prices_include_vat: basisToPricesIncludeVat(productForm.displayBasis),
            pricing_matrix: serializedPricingMatrix,
            pricing_deltas: serializedPricingDeltas,
          };

          delete productData.pricingMatrix;
          delete productData.pricingDeltas;

          if (isCatalogMaster) {
            const saved = await saveCatalogDraftFromEditor(productData);
            catalogDraft.value = saved.draft || saved;
            if (!silent) {
              toastStore.addToast({
                type: 'success',
                message: 'Concept opgeslagen',
              });
            }
            return true;
          }

          // Log upsells before saving - check if allowQuantity is present
          console.log('💾 Saving product with upsells:', {
            productId: productForm.id,
            upsellsCount: productData.upsells?.length || 0,
            upsells: JSON.parse(JSON.stringify(productData.upsells || [])), // Deep clone
            upsellsDetails: productData.upsells?.map((u) => ({
              id: u.id,
              productId: u.productId,
              allowQuantity: u.allowQuantity,
              allowQuantityType: typeof u.allowQuantity,
              quantityConfig: u.quantityConfig,
              displaySettings: u.displaySettings,
              showOnQuote: u.showOnQuote,
              showOnPackingSlip: u.showOnPackingSlip,
              conditionalDisplay: u.conditionalDisplay ? 'present' : 'missing',
              // Check all keys
              allKeys: Object.keys(u),
            })),
          });

          if (actualIsNewProduct.value) {
            const createdProduct = await addProductToFeed(feedId, productData);

            // Update productForm with the created product ID
            if (createdProduct && createdProduct.id) {
              productForm.id = createdProduct.id;

              // Update route to edit mode (without page reload)
              router.replace(`/products/${feedId}/${createdProduct.id}`);

              toastStore.addToast({
                message: 'Product succesvol aangemaakt',
                type: 'success',
              });
            } else {
              // Fallback: redirect if ID not in response
              router.push(`/products/${feedId}/${productData.id || 0}`);
            }
          } else {
            // Use product UUID if available, otherwise fall back to index
            if (productForm.id) {
              await updateProductInFeed(feedId, productForm.id, productData);

              // Check for configurator migrations after product update
              await checkAndHandleMigrations(productForm.id);
            } else if (!isNaN(parseInt(productParam))) {
              await updateProductInFeed(feedId, parseInt(productParam), productData);
            } else {
              throw new Error('Cannot update product: no valid ID or index');
            }

            toastStore.addToast({
              message: 'Product succesvol opgeslagen',
              type: 'success',
            });
          }
        } catch (error) {
          console.error('Error saving product:', error);
          alert('Er is een fout opgetreden bij het opslaan');
          return false;
        } finally {
          saving.value = false;
        }
      };

      const saveCatalogDraftFromEditor = async (productData) => {
        const previous = catalogDraft.value || {};
        const draft = {
          ...previous,
          title: productData.title,
          category: catalogCategory.value.trim() || null,
          description: productData.description,
          image_url: productData.imageUrl || previous.image_url || '',
          price: productData.price,
          options: productData.options || [],
          option_groups: productData.optionGroups || [],
          use_matrix_pricing: Boolean(productData.useMatrixPricing),
          pricing_matrix: productData.pricing_matrix,
          pricing_deltas: productData.pricing_deltas,
          computed_fields: productData.computedFields || [],
          choice_constraints: productData.choiceConstraints || [],
          cross_option_rules: productData.crossOptionRules || [],
          auto_include_rules: productData.autoIncludeRules || [],
          volume_pricing_rules: productData.volumePricingRules || [],
          upsells: (productData.upsells || []).map((upsell) => {
            if (!upsell || typeof upsell !== 'object') return upsell;
            const masterId = upsell.catalog_master_id || upsell.productId;
            if (!masterId) return upsell;
            return { ...upsell, catalog_master_id: masterId };
          }),
          upsell_groups: productData.upsellGroups || [],
          vat_rate: productData.vat_rate,
          prices_include_vat: catalogPriceBasis.value === 'brochure_incl_vat',
          price_basis: catalogPriceBasis.value,
          order_mapping: catalogOrderMapping.value || previous.order_mapping || null,
        };
        return platformAdminService.saveCatalogDraft(route.params.masterId, draft);
      };

      const publishCatalogMaster = () => {
        const modalId = uiStore.addModal(CatalogPublishModal, {
          title: 'Nieuwe versie publiceren',
          size: 'md',
          onSubmit: async (effectiveFrom) => {
            uiStore.closeModal(modalId);
            await runCatalogPublish(effectiveFrom);
          },
        });
      };

      const runCatalogPublish = async (effectiveFrom) => {
        publishingCatalog.value = true;
        try {
          const saved = await saveProduct();
          if (saved === false) return;
          const version = await platformAdminService.publishCatalogMaster(route.params.masterId, {
            effectiveFrom,
          });
          catalogVersions.value = [
            version,
            ...catalogVersions.value.map((v) =>
              v.adopted_at || v.superseded_at ? v : { ...v, superseded_at: version.published_at }
            ),
          ];
          if (version.scheduled) {
            const date = new Date(version.effective_from).toLocaleDateString('nl-NL', {
              dateStyle: 'long',
            });
            toastStore.addToast({
              type: 'success',
              message: `Versie ${version.version_number} ingepland. Dealers gaan over op ${date}.`,
              duration: 5000,
            });
            return;
          }
          const updated = version.adoption?.updated || 0;
          const skipped = version.adoption?.skipped?.length || 0;
          const dealerNote =
            skipped > 0
              ? `${updated} dealers bijgewerkt, ${skipped} niet.`
              : `${updated} dealers bijgewerkt.`;
          toastStore.addToast({
            type: 'success',
            message: `Versie ${version.version_number} gepubliceerd. ${dealerNote}`,
          });
        } catch (error) {
          console.error('Error publishing catalog master:', error);
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || 'Publiceren mislukt',
          });
        } finally {
          publishingCatalog.value = false;
        }
      };

      const onCatalogAgreementSaved = (installation) => {
        const catalog = productForm.attributeValues?.catalog || {};
        productForm.attributeValues = {
          ...(productForm.attributeValues || {}),
          catalog: {
            ...catalog,
            purchase_margin_pct: installation?.purchase_margin_pct ?? catalog.purchase_margin_pct,
            sell_adjustment: installation?.sell_adjustment ?? catalog.sell_adjustment,
          },
        };
      };

      // The product row was rewritten by the API; reload so the form shows merged rules.
      const onCatalogRulesSaved = async () => {
        if (!productForm.id) return;
        try {
          const product = await getProductById(productForm.id);
          applyLoadedProduct(product);
        } catch (error) {
          console.error('Error reloading product after rules save:', error);
        }
      };

      const catalogMeta = computed(() => productForm.attributeValues?.catalog || {});
      const catalogMatrixDetached = computed(() => catalogMeta.value.matrix_detached === true);
      const catalogBasisLabel = computed(() =>
        (PRICE_BASIS_LABELS[catalogMeta.value.price_basis] || 'brochureprijs').toLowerCase()
      );
      const catalogPricingNotice = computed(() => {
        const base =
          'Berekende velden en optiebeperkingen komen uit de prijslijst en lopen mee met nieuwe versies.';
        if (catalogMeta.value.link_status === 'partial') {
          return `${base} Dit product is deels losgekoppeld: jouw eigen matrix of meerprijzen blijven staan bij een nieuwe prijslijst.`;
        }
        return `${base} De matrix en meerprijzen kun je hieronder overschrijven; dat deel loopt daarna niet meer mee.`;
      });

      const matrixEditing = ref(false);
      const matrixSaving = ref(false);
      const matrixEditorKey = ref(0);
      const matrixAxesAtStart = ref(null);

      const axesOf = (matrix) =>
        ['row', 'col', 'group'].map((key) => matrix?.dimensions?.[key] || '').join('|');

      const reloadCatalogProduct = async () => {
        await onCatalogRulesSaved();
        matrixEditorKey.value += 1;
      };

      const startMatrixEdit = () => {
        matrixAxesAtStart.value = axesOf(productForm.pricingMatrix);
        matrixEditing.value = true;
      };

      const cancelMatrixEdit = async () => {
        matrixEditing.value = false;
        await reloadCatalogProduct();
      };

      const saveMatrixOverride = async () => {
        if (!catalogInstallationId.value) return;
        const matrix = productForm.pricingMatrix ? { ...productForm.pricingMatrix } : null;
        if (matrix) delete matrix.deltaTables;
        const serialized = serializePricingMatrixToApi(matrix);
        if (!serialized?.grid?.length) {
          toastStore.addToast({ type: 'error', message: 'De matrix is leeg' });
          return;
        }
        if (axesOf(productForm.pricingMatrix) !== matrixAxesAtStart.value) {
          toastStore.addToast({
            type: 'error',
            message: 'De assen van de matrix moeten gelijk blijven aan die van de leverancier',
          });
          return;
        }
        matrixSaving.value = true;
        try {
          await catalogService.updatePriceOverrides(catalogInstallationId.value, (current) => [
            ...current.filter((item) => item?.scope !== 'matrix'),
            { scope: 'matrix', pricing_matrix: serialized },
          ]);
          matrixEditing.value = false;
          await reloadCatalogProduct();
          toastStore.addToast({ type: 'success', message: 'Eigen matrix opgeslagen' });
        } catch (error) {
          const details = error?.response?.data?.details;
          toastStore.addToast({
            type: 'error',
            message:
              (Array.isArray(details) && details[0]?.message) ||
              error?.response?.data?.error ||
              'Matrix opslaan mislukt',
          });
        } finally {
          matrixSaving.value = false;
        }
      };

      const resetMatrixOverride = () => {
        const modalId = uiStore.addModal(false, {
          title: 'Terug naar de matrix van de leverancier?',
          description:
            'Je eigen matrix wordt verwijderd. Daarna gelden weer de prijzen uit de prijslijst en loopt de matrix mee met nieuwe versies.',
          submitText: 'Terugzetten',
          cancelText: 'Annuleren',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            matrixSaving.value = true;
            try {
              await catalogService.updatePriceOverrides(catalogInstallationId.value, (current) =>
                current.filter((item) => item?.scope !== 'matrix')
              );
              await reloadCatalogProduct();
              toastStore.addToast({
                type: 'success',
                message: 'Matrix van de leverancier hersteld',
              });
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: error?.response?.data?.error || 'Terugzetten mislukt',
              });
            } finally {
              matrixSaving.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const onCatalogMappingSave = async (mapping) => {
        catalogOrderMapping.value = {
          ...(catalogOrderMapping.value || {}),
          ...mapping,
        };
        const draft = {
          ...(catalogDraft.value || {}),
          order_mapping: catalogOrderMapping.value,
        };
        const saved = await platformAdminService.saveCatalogDraft(route.params.masterId, draft);
        catalogDraft.value = saved.draft || draft;
      };

      const bulkUpdateCatalogPrices = async () => {
        const currentPrice = Number(inputs.value.price.binding);
        const productName = inputs.value.title.binding || productForm.title || 'dit product';
        let isUpdating = false;
        const modalId = uiStore.addModal(BulkPriceUpdateModal, {
          title: 'Prijsupdate',
          size: 'sm',
          description:
            `Met hoeveel procent wil je de prijzen van "${productName}" aanpassen? ` +
            'Dit geldt voor de matrix, meerprijzen en opties. Dealers zien het na publiceren.',
          defaultPercentage: 5,
          currentPrice: Number.isFinite(currentPrice) ? currentPrice : null,
          beforeClose: () => !isUpdating,
          onConfirm: async (percentage) => {
            isUpdating = true;
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              const saved = await saveProduct({ silent: true });
              if (saved === false) {
                uiStore.updateModalProps(modalId, { loading: false });
                isUpdating = false;
                return;
              }
              const previewResult = await platformAdminService.scaleCatalogPrices(
                route.params.masterId,
                percentage,
                { dryRun: true }
              );
              const fieldsCount = previewResult.scaled_pricing_fields || 0;
              isUpdating = false;
              uiStore.closeModal(modalId);

              const confirmModalId = uiStore.addModal(false, {
                title: 'Prijsupdate uitvoeren?',
                description:
                  `Er worden ${fieldsCount} prijsvelden in het concept bijgewerkt. ` +
                  'Dealers krijgen dit pas na publiceren.',
                submitText: 'Ja, prijsupdate uitvoeren',
                cancelText: 'Annuleren',
                status: 'danger',
                onSubmit: async () => {
                  uiStore.updateModalProps(confirmModalId, { loading: true, closable: false });
                  try {
                    await platformAdminService.scaleCatalogPrices(
                      route.params.masterId,
                      percentage
                    );
                    await loadCatalogMaster();
                    matrixEditorKey.value += 1;
                    uiStore.closeModal(confirmModalId);
                    toastStore.addToast({
                      type: 'success',
                      message: 'Prijzen in het concept bijgewerkt',
                    });
                  } catch (error) {
                    console.error('Error scaling catalog prices:', error);
                    uiStore.updateModalProps(confirmModalId, { loading: false, closable: true });
                    toastStore.addToast({
                      type: 'error',
                      message: error?.response?.data?.error || 'Prijsupdate mislukt',
                    });
                  }
                },
                onCancel: () => uiStore.closeModal(confirmModalId),
              });
            } catch (error) {
              console.error('Error previewing catalog price update:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                type: 'error',
                message: error?.response?.data?.error || 'Prijsupdate-preview mislukt',
              });
              isUpdating = false;
            }
          },
        });
      };

      const bulkUpdateCurrentProductPrice = async () => {
        if (isCatalogMaster) {
          await bulkUpdateCatalogPrices();
          return;
        }
        if (!productForm.id) {
          toastStore.addToast({
            type: 'error',
            message: 'Sla het product eerst op voordat je een prijsupdate uitvoert',
          });
          return;
        }

        const currentPrice = Number(inputs.value.price.binding);

        let isUpdating = false;
        const modalId = uiStore.addModal(BulkPriceUpdateModal, {
          title: 'Prijsupdate',
          size: 'sm',
          description: `Met hoeveel procent wil je de prijs van "${
            inputs.value.title.binding || productForm.title
          }" aanpassen?`,
          defaultPercentage: 5,
          currentPrice: Number.isFinite(currentPrice) ? currentPrice : null,
          beforeClose: () => !isUpdating,
          onConfirm: async (percentage) => {
            const payload = {
              mode: 'percentage',
              field: 'price',
              value: percentage,
              filter: { product_ids: [productForm.id] },
            };

            isUpdating = true;
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              const previewResult = await bulkUpdateProducts(payload);
              const fieldsCount =
                previewResult.scaled_pricing_fields ??
                previewResult.preview?.[0]?.scaled_pricing_fields ??
                0;

              isUpdating = false;
              uiStore.closeModal(modalId);

              const confirmModalId = uiStore.addModal(false, {
                title: 'Prijsupdate uitvoeren?',
                description:
                  `Er worden ${fieldsCount} prijsvelden bijgewerkt. Weet je het zeker? ` +
                  'Dit kan niet ongedaan worden gemaakt.',
                submitText: 'Ja, prijsupdate uitvoeren',
                cancelText: 'Annuleren',
                status: 'danger',
                onSubmit: async () => {
                  uiStore.updateModalProps(confirmModalId, { loading: true, closable: false });
                  try {
                    await bulkUpdateProducts({
                      ...payload,
                      dry_run: false,
                    });
                    const refreshedProduct = await getProductById(productForm.id);
                    await loadProductIntoForm(refreshedProduct);
                    uiStore.closeModal(confirmModalId);
                    toastStore.addToast({ type: 'success', message: 'Productprijs bijgewerkt' });
                  } catch (error) {
                    console.error('Error bulk updating product:', error);
                    uiStore.updateModalProps(confirmModalId, {
                      loading: false,
                      closable: true,
                    });
                    toastStore.addToast({
                      type: 'error',
                      message: 'Er is een fout opgetreden bij de prijsupdate',
                    });
                  }
                },
                onCancel: () => {
                  uiStore.closeModal(confirmModalId);
                },
              });
            } catch (error) {
              console.error('Error previewing product price update:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                type: 'error',
                message: 'Er is een fout opgetreden bij de prijsupdate-preview',
              });
              isUpdating = false;
            }
          },
        });
      };

      // USP management is now handled by USPInput component

      // Upsell management
      const getUpsellKey = (upsell, idx) => {
        // Handle both old format (string) and new format (object)
        if (typeof upsell === 'string') {
          return `upsell-${idx}-${upsell}`;
        }
        return upsell.id || upsell.productId || `upsell-${idx}`;
      };

      const formatUpsellPrice = (price) => {
        if (!price) return '0,00';
        return price.toFixed(2).replace('.', ',');
      };

      const addUpsell = () => {
        const modalId = uiStore.addModal(UpsellModal, {
          title: 'Upsell toevoegen',
          upsell: null,
          availableProducts: availableProductsForUpsells.value,
          currentProductId: productForm.id,
          productOptions: productForm.options || [],
          onSubmit: (upsellData) => {
            // Normalize upsells array if needed (handle backwards compatibility)
            if (!Array.isArray(productForm.upsells)) {
              productForm.upsells = [];
            }

            // Convert old format (string[]) to new format if needed
            const normalizedUpsells = productForm.upsells.map((upsell) => {
              if (typeof upsell === 'string') {
                return {
                  id: upsell,
                  productId: upsell,
                  showOnQuote: true,
                  showOnPackingSlip: false,
                  allowQuantity: false,
                };
              }
              return upsell;
            });

            normalizedUpsells.push(upsellData);
            productForm.upsells = normalizedUpsells;
            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const editUpsell = (idx) => {
        // Normalize upsell if it's old format
        let upsell = productForm.upsells[idx];
        if (typeof upsell === 'string') {
          upsell = {
            id: upsell,
            productId: upsell,
            showOnQuote: true,
            showOnPackingSlip: false,
            allowQuantity: false,
          };
        }

        const modalId = uiStore.addModal(UpsellModal, {
          title: 'Upsell bewerken',
          upsell: JSON.parse(JSON.stringify(upsell)), // Clone for editing
          availableProducts: availableProductsForUpsells.value,
          currentProductId: productForm.id,
          productOptions: productForm.options || [],
          onSubmit: (upsellData) => {
            // Normalize upsells array
            const normalizedUpsells = productForm.upsells.map((u) =>
              typeof u === 'string'
                ? {
                    id: u,
                    productId: u,
                    showOnQuote: true,
                    showOnPackingSlip: false,
                    allowQuantity: false,
                  }
                : u
            );

            normalizedUpsells.splice(idx, 1, upsellData);
            productForm.upsells = normalizedUpsells;
            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const removeUpsell = (idx) => {
        productForm.upsells.splice(idx, 1);
      };

      // Product option management
      const addProductOption = () => {
        editingProductOptionIndex.value = null;
        const modalId = uiStore.addModal(ProductOptionModal, {
          title: 'Optie toevoegen',
          option: null, // New option
          availableOptions: productForm.options || [], // All options (filtering happens in modal)
          optionGroups: productForm.optionGroups || [], // Pass option groups
          computedFields: productForm.computedFields || [],
          choiceSets: isCatalogMaster ? catalogChoiceSets.value : [],
          size: 'xl',
          onSubmit: (optionData) => {
            productForm.options.push(optionData);
            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
          onCreateGroup: async (groupData) => {
            // Create new group
            const newGroupId = generateGroupId();
            const newGroupName = groupData.name.trim();

            if (!productForm.optionGroups) {
              productForm.optionGroups = [];
            }
            productForm.optionGroups.push({ id: newGroupId, name: newGroupName });
            groupExpanded.value[newGroupId] = true;

            // Update the modal with new groups
            // Note: Modal props can't be updated directly, so user will need to select manually
          },
        });
      };

      const copyProductOptionsFromOther = () => {
        const modalId = uiStore.addModal(CopyProductOptionModal, {
          title: $t('products.copy_options.title'),
          size: 'lg',
          products: availableProductsForUpsells.value || [],
          currentProductId: productForm.id || null,
          existingOptionGroups: productForm.optionGroups || [],
          onSubmit: ({ options, newOptionGroups }) => {
            if (!productForm.options) {
              productForm.options = [];
            }
            if (!productForm.optionGroups) {
              productForm.optionGroups = [];
            }

            (newOptionGroups || []).forEach((group) => {
              productForm.optionGroups.push(group);
              groupExpanded.value[group.id] = true;
            });

            productForm.options.push(...(options || []));

            toastStore.addToast({
              type: 'success',
              message: $t('products.copy_options.success', { n: options?.length || 0 }),
            });

            uiStore.closeModal(modalId);
          },
        });
      };

      const editProductOption = (idx) => {
        editingProductOptionIndex.value = idx;
        const option = productForm.options[idx];

        // Pass all options (filtering happens in modal)
        const availableForMatrix = productForm.options || [];

        const modalId = uiStore.addModal(ProductOptionModal, {
          title: 'Optie bewerken',
          option: JSON.parse(JSON.stringify(option)), // Clone for editing
          availableOptions: availableForMatrix, // Pass other number options for matrix pricing
          optionGroups: productForm.optionGroups || [], // Pass option groups
          computedFields: productForm.computedFields || [],
          choiceSets: isCatalogMaster ? catalogChoiceSets.value : [],
          size: 'xl',
          onSubmit: (optionData) => {
            productForm.options.splice(idx, 1, optionData);
            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
          onCreateGroup: async (groupData) => {
            // Create new group
            const newGroupId = generateGroupId();
            const newGroupName = groupData.name.trim();

            if (!productForm.optionGroups) {
              productForm.optionGroups = [];
            }
            productForm.optionGroups.push({ id: newGroupId, name: newGroupName });
            groupExpanded.value[newGroupId] = true;

            // Update modal props with new groups
            uiStore.updateModalProps(modalId, {
              optionGroups: productForm.optionGroups,
            });

            // Note: The modal will automatically update because optionGroups is reactive
            // The user can now select the newly created group
          },
        });
      };

      const removeProductOption = (idx) => {
        productForm.options.splice(idx, 1);
      };

      const onOptionsDragEnd = () => {
        // Options are automatically reordered by v-model binding
        // Optionally trigger save or other actions here
      };

      // Group expanded state (for options)
      const groupExpanded = ref({});

      // Upsell group expanded state
      const upsellGroupExpanded = ref({});

      // Computed: ungrouped options (no pageGroupId)
      const ungroupedOptions = computed(() => {
        return (
          productForm.options?.filter(
            (opt) => !opt.pageGroupId && opt.catalog_origin !== 'dealer'
          ) || []
        );
      });

      // Computed: ungrouped upsells (no groupId)
      const ungroupedUpsells = computed(() => {
        return productForm.upsells?.filter((upsell) => !upsell.groupId) || [];
      });

      // Get options in a specific group by group ID
      const getOptionsInGroup = (groupId) => {
        return (
          productForm.options?.filter(
            (opt) => opt.pageGroupId === groupId && opt.catalog_origin !== 'dealer'
          ) || []
        );
      };

      // --- Auto-fill (CU-869dz0un3): identical single-choice fields fill each other ---
      // Applies to all single-choice layouts (dropdown, radio/list, grid). Multi-choice
      // stays out of scope — values are arrays and overwrite rules get ambiguous.
      //
      // Auto-fill clusters, computed once from the options list and cached by
      // Vue: single-choice options in the SAME real group that share BOTH a
      // choice signature AND visibility (internalOnly/externalOnly). Only options
      // with a real group can land on the same runtime page, so ungrouped
      // options never cluster. Visibility must match because the funnel filters
      // options per audience — a mixed-visibility pair would render <2 fields in
      // some funnel and never auto-fill there. Maps each member id → the array of
      // ids in its cluster (incl. itself). Because it's a computed, the template
      // and the self-heal watcher recompute only when `productForm.options`
      // actually changes — not per card, per render.
      // Related = same product set or at least one shared choice label, across
      // all groups and both channels (see shared/utils/autoFill.js). A hidden
      // sibling (internalOnly on the website) is seeded too, so its price counts.
      const autoFillClusters = computed(() => clusterAutoFillOptions(productForm.options || []));

      // The cluster this option belongs to (its ids, incl. itself). ≥2 = a real
      // cluster. Used by toggleAutoFill to flip the whole set together.
      const sameSignatureSingleChoiceIds = (option) => autoFillClusters.value[option?.id] || [];

      // An option can offer auto-fill when it has at least one related sibling.
      const isAutoFillEligible = (option) => (autoFillClusters.value[option?.id]?.length || 0) >= 2;

      const isAutoFillOn = (option) => option?.autoFill === true && isAutoFillEligible(option);

      // Labels of the related siblings, for the badge tooltip.
      const autoFillSiblingLabels = (option) =>
        sameSignatureSingleChoiceIds(option)
          .filter((id) => id !== option?.id)
          .map((id) => productForm.options.find((o) => o.id === id)?.label || id);

      // Toggle auto-fill for this option only. Relatedness is loose (one shared
      // label is enough), so flipping a whole cluster could drag in a field the
      // dealer never meant to link; he flags each field explicitly instead.
      const toggleAutoFill = (option) => {
        if (!isAutoFillEligible(option)) return;
        const i = productForm.options.findIndex((o) => o.id === option.id);
        if (i === -1) return;
        productForm.options.splice(i, 1, {
          ...productForm.options[i],
          autoFill: option.autoFill !== true,
        });
      };

      // Gates the "auto-fill uitgeschakeld" toast below. Stays false during the
      // initial product load so healing legacy stale flags doesn't fire a toast
      // on open; armed once, a tick after mount settles (see onMounted).
      const autoFillHealArmed = ref(false);

      // Self-heal a stale `autoFill: true` left behind when a cluster is broken
      // (a sibling deleted, its choices/label/layout/visibility edited so it no
      // longer matches, or moved to another group). Once broken, the option is
      // no longer eligible so its toggle disappears — without this the flag
      // would linger in the saved product and silently reactivate if an
      // identical option is later re-added. Only ever clears (never enables), so
      // it can't surprise the user; keyed off the same eligibility check the UI
      // uses, so it catches EVERY way a cluster can break without enumerating
      // them. When armed (i.e. after a user edit, not on load) it also toasts
      // the fields it switched off.
      const normalizeAutoFillFlags = () => {
        const turnedOff = [];
        (productForm.options || []).forEach((option) => {
          if (option?.autoFill === true && !isAutoFillEligible(option)) {
            option.autoFill = false;
            turnedOff.push(option.label || 'Naamloos veld');
          }
        });

        if (autoFillHealArmed.value && turnedOff.length) {
          const names = [...new Set(turnedOff)];
          toastStore.addToast({
            type: 'info',
            message:
              names.length === 1
                ? `Auto-fill uitgeschakeld voor "${names[0]}" — er is geen verwant veld meer.`
                : `Auto-fill uitgeschakeld voor ${names.length} velden — er is geen verwant veld meer.`,
          });
        }
      };

      // Re-run whenever the cluster map changes — options added, removed,
      // regrouped, or edited so signatures/visibility shift (which is exactly
      // when a flag can go stale). Watching the computed instead of deep-watching
      // the whole options tree reuses its dependency tracking (no separate deep
      // traversal), and normalize's own `autoFill` writes don't feed back in (the
      // cluster map doesn't depend on the flag), so it can't loop. Also runs on
      // load to heal flags saved before this fix.
      watch(autoFillClusters, normalizeAutoFillFlags);

      // Get upsells in a specific group by group ID
      const getUpsellsInGroup = (groupId) => {
        return productForm.upsells?.filter((upsell) => upsell.groupId === groupId) || [];
      };

      // Toggle group expanded/collapsed (options)
      const toggleGroup = (groupId) => {
        groupExpanded.value[groupId] = !groupExpanded.value[groupId];
      };

      // Toggle upsell group expanded/collapsed
      const toggleUpsellGroup = (groupId) => {
        upsellGroupExpanded.value[groupId] = !upsellGroupExpanded.value[groupId];
      };

      // Native drag & drop state
      const draggingOptionId = ref(null);
      const dropIndicator = ref({ optionId: null, position: null });

      // Handle drag start
      const handleDragStart = (option, event) => {
        draggingOptionId.value = option.id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', option.id);
      };

      // Handle drag end
      const handleDragEnd = () => {
        draggingOptionId.value = null;
        dropIndicator.value = { optionId: null, position: null };
      };

      // Handle drag over option (for drop indicator)
      const handleDragOverOption = (optionId, event) => {
        event.preventDefault();
        if (draggingOptionId.value === optionId) return; // Don't show indicator on self

        const rect = event.currentTarget.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        const position = event.clientY < midpoint ? 'before' : 'after';

        dropIndicator.value = { optionId, position };
      };

      // Handle drag leave option
      const handleDragLeaveOption = (event) => {
        // Only clear if we're actually leaving the element (not entering a child)
        const relatedTarget = event.relatedTarget;
        if (!event.currentTarget.contains(relatedTarget)) {
          dropIndicator.value = { optionId: null, position: null };
        }
      };

      // Handle drop on specific option (for reordering)
      const handleDropOnOption = (targetOptionId, targetGroupId, event) => {
        event.preventDefault();
        event.stopPropagation();

        const draggedOptionId = event.dataTransfer.getData('text/plain');
        if (!draggedOptionId || draggedOptionId === targetOptionId) {
          dropIndicator.value = { optionId: null, position: null };
          return;
        }

        const draggedIndex = productForm.options.findIndex((opt) => opt.id === draggedOptionId);
        const targetIndex = productForm.options.findIndex((opt) => opt.id === targetOptionId);

        if (draggedIndex === -1 || targetIndex === -1) {
          dropIndicator.value = { optionId: null, position: null };
          return;
        }

        // Get the dragged option
        const [draggedOption] = productForm.options.splice(draggedIndex, 1);

        // Update pageGroupId if moving to different group
        draggedOption.pageGroupId = targetGroupId;

        // Calculate new index (account for removal)
        let newIndex = targetIndex;
        if (draggedIndex < targetIndex) {
          newIndex--;
        }
        if (dropIndicator.value.position === 'after') {
          newIndex++;
        }

        // Insert at new position
        productForm.options.splice(newIndex, 0, draggedOption);

        // Clear states
        draggingOptionId.value = null;
        dropIndicator.value = { optionId: null, position: null };
      };

      // Handle drop on group (for moving to empty group or end of list)
      const handleDropOnGroup = (targetGroupId, event) => {
        event.preventDefault();
        const optionId = event.dataTransfer.getData('text/plain');
        if (optionId) {
          const optionIndex = productForm.options.findIndex((opt) => opt.id === optionId);
          if (optionIndex !== -1) {
            productForm.options[optionIndex].pageGroupId = targetGroupId;
          }
        }
        draggingOptionId.value = null;
        dropIndicator.value = { optionId: null, position: null };
      };

      // ==================== UPSELL DRAG & DROP ====================
      const draggingUpsellId = ref(null);
      const upsellDropIndicator = ref({ upsellId: null, position: null });

      // Handle upsell drag start
      const handleUpsellDragStart = (upsell, event) => {
        draggingUpsellId.value = upsell.id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', upsell.id);
      };

      // Handle upsell drag end
      const handleUpsellDragEnd = () => {
        draggingUpsellId.value = null;
        upsellDropIndicator.value = { upsellId: null, position: null };
      };

      // Handle drag over upsell (for drop indicator)
      const handleDragOverUpsell = (upsellId, event) => {
        event.preventDefault();
        if (draggingUpsellId.value === upsellId) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        const position = event.clientY < midpoint ? 'before' : 'after';

        upsellDropIndicator.value = { upsellId, position };
      };

      // Handle drag leave upsell
      const handleDragLeaveUpsell = (event) => {
        const relatedTarget = event.relatedTarget;
        if (!event.currentTarget.contains(relatedTarget)) {
          upsellDropIndicator.value = { upsellId: null, position: null };
        }
      };

      // Handle drop on specific upsell (for reordering)
      const handleDropOnUpsell = (targetUpsellId, targetGroupId, event) => {
        event.preventDefault();
        event.stopPropagation();

        const draggedUpsellId = event.dataTransfer.getData('text/plain');
        if (!draggedUpsellId || draggedUpsellId === targetUpsellId) {
          upsellDropIndicator.value = { upsellId: null, position: null };
          return;
        }

        const draggedIndex = productForm.upsells.findIndex((u) => u.id === draggedUpsellId);
        const targetIndex = productForm.upsells.findIndex((u) => u.id === targetUpsellId);

        if (draggedIndex === -1 || targetIndex === -1) {
          upsellDropIndicator.value = { upsellId: null, position: null };
          return;
        }

        // Get the dragged upsell
        const [draggedUpsell] = productForm.upsells.splice(draggedIndex, 1);

        // Update groupId if moving to different group
        draggedUpsell.groupId = targetGroupId;

        // Calculate new index (account for removal)
        let newIndex = targetIndex;
        if (draggedIndex < targetIndex) {
          newIndex--;
        }
        if (upsellDropIndicator.value.position === 'after') {
          newIndex++;
        }

        // Insert at new position
        productForm.upsells.splice(newIndex, 0, draggedUpsell);

        // Clear states
        draggingUpsellId.value = null;
        upsellDropIndicator.value = { upsellId: null, position: null };
      };

      // Handle drop on upsell group (for moving to empty group or end of list)
      const handleDropOnUpsellGroup = (targetGroupId, event) => {
        event.preventDefault();
        const upsellId = event.dataTransfer.getData('text/plain');
        if (upsellId) {
          const upsellIndex = productForm.upsells.findIndex((u) => u.id === upsellId);
          if (upsellIndex !== -1) {
            productForm.upsells[upsellIndex].groupId = targetGroupId;
          }
        }
        draggingUpsellId.value = null;
        upsellDropIndicator.value = { upsellId: null, position: null };
      };

      // Edit product option by ID (for grouped view)
      const editProductOptionById = (optionId) => {
        const idx = productForm.options.findIndex((opt) => opt.id === optionId);
        if (idx !== -1) {
          editProductOption(idx);
        }
      };

      // Remove product option by ID (for grouped view)
      const removeProductOptionById = (optionId) => {
        const idx = productForm.options.findIndex((opt) => opt.id === optionId);
        if (idx !== -1) {
          removeProductOption(idx);
        }
      };

      // Add new option group via modal
      // Generate unique ID for option groups
      const generateGroupId = () => {
        return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      };

      const addOptionGroup = () => {
        const modalId = uiStore.addModal(OptionGroupModal, {
          title: 'Groep toevoegen',
          group: null,
          onSubmit: (groupData) => {
            const newGroupName = groupData.name.trim();
            const newGroupId = generateGroupId();

            if (!productForm.optionGroups) {
              productForm.optionGroups = [];
            }
            productForm.optionGroups.push({ id: newGroupId, name: newGroupName });
            groupExpanded.value[newGroupId] = true;

            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      // Edit existing option group
      const editOptionGroup = (groupId) => {
        const group = productForm.optionGroups?.find((g) => g.id === groupId);
        if (!group) return;

        const modalId = uiStore.addModal(OptionGroupModal, {
          title: 'Groep bewerken',
          group: { id: groupId, name: group.name },
          onSubmit: (groupData) => {
            const newGroupName = groupData.name.trim();

            // Update group name in optionGroups array (ID stays the same)
            const groupIndex = productForm.optionGroups?.findIndex((g) => g.id === groupId);
            if (groupIndex !== -1) {
              productForm.optionGroups[groupIndex].name = newGroupName;
            }

            // No need to update options - they reference by ID which doesn't change

            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      // Remove option group (options become ungrouped)
      const removeOptionGroup = (groupId) => {
        const group = productForm.optionGroups?.find((g) => g.id === groupId);
        if (!group) return;

        if (
          confirm(
            `Weet je zeker dat je de groep "${group.name}" wilt verwijderen? De opties worden los gezet.`
          )
        ) {
          // Remove pageGroupId from all options in this group
          productForm.options?.forEach((option) => {
            if (option.pageGroupId === groupId) {
              option.pageGroupId = null;
            }
          });

          // Remove from optionGroups array
          const groupIndex = productForm.optionGroups?.findIndex((g) => g.id === groupId);
          if (groupIndex !== -1) {
            productForm.optionGroups.splice(groupIndex, 1);
          }

          delete groupExpanded.value[groupId];
        }
      };

      // ========== UPSELL GROUP MANAGEMENT ==========

      // Add new upsell group via modal
      const addUpsellGroup = () => {
        const modalId = uiStore.addModal(OptionGroupModal, {
          title: 'Upsell groep toevoegen',
          group: null,
          onSubmit: (groupData) => {
            const newGroupName = groupData.name.trim();
            const newGroupId = generateGroupId();

            if (!productForm.upsellGroups) {
              productForm.upsellGroups = [];
            }
            productForm.upsellGroups.push({ id: newGroupId, name: newGroupName });
            upsellGroupExpanded.value[newGroupId] = true;

            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      // Edit existing upsell group
      const editUpsellGroup = (groupId) => {
        const group = productForm.upsellGroups?.find((g) => g.id === groupId);
        if (!group) return;

        const modalId = uiStore.addModal(OptionGroupModal, {
          title: 'Upsell groep bewerken',
          group: { id: groupId, name: group.name },
          onSubmit: (groupData) => {
            const newGroupName = groupData.name.trim();

            // Update group name in upsellGroups array (ID stays the same)
            const groupIndex = productForm.upsellGroups?.findIndex((g) => g.id === groupId);
            if (groupIndex !== -1) {
              productForm.upsellGroups[groupIndex].name = newGroupName;
            }

            uiStore.closeModal(modalId);
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      // Remove upsell group (upsells become ungrouped)
      const removeUpsellGroup = (groupId) => {
        const group = productForm.upsellGroups?.find((g) => g.id === groupId);
        if (!group) return;

        if (
          confirm(
            `Weet je zeker dat je de groep "${group.name}" wilt verwijderen? De upsells worden los gezet.`
          )
        ) {
          // Remove groupId from all upsells in this group
          productForm.upsells?.forEach((upsell) => {
            if (upsell.groupId === groupId) {
              upsell.groupId = null;
            }
          });

          // Remove from upsellGroups array
          const groupIndex = productForm.upsellGroups?.findIndex((g) => g.id === groupId);
          if (groupIndex !== -1) {
            productForm.upsellGroups.splice(groupIndex, 1);
          }

          delete upsellGroupExpanded.value[groupId];
        }
      };

      const handleMatrixPricingUpdate = (matrixData) => {
        productForm.useMatrixPricing = matrixData.useMatrixPricing;
        productForm.pricingMatrix = matrixData.pricingMatrix;
        productForm.pricingDeltas = matrixData.pricingDeltas || [];
      };

      // Get option label by ID
      const getOptionLabelById = (optionId) => {
        const option = productForm.options?.find((o) => o.id === optionId);
        return option?.label || optionId;
      };

      // Dynamic subtitle for matrix pricing card
      const matrixPricingSubtitle = computed(() => {
        if (!productForm.useMatrixPricing || !productForm.pricingMatrix?.dimensions) {
          return 'Prijzen op basis van afmetingen configureren';
        }
        const dims = productForm.pricingMatrix.dimensions;
        const rowLabel = getOptionLabelById(dims.row);
        const colLabel = getOptionLabelById(dims.col);
        return `Prijzen op basis van combinaties (${rowLabel?.toLowerCase()} × ${colLabel?.toLowerCase()})`;
      });

      // Category management
      const slugifyCategoryName = (name) =>
        (name || '').toString().trim().toLowerCase().replace(/\s+/g, '-');

      const openCategoryModal = () => {
        const modalId = uiStore.addModal(CategoryManagerModal, {
          title: 'Categorieën beheren',
          size: 'lg',
          categories: availableCategories.value,
          onCreate: async (payload) => {
            const newCategory = await createCategory({
              name: payload.name,
              slug: payload.slug || slugifyCategoryName(payload.name),
              color: payload.color || '#3B82F6',
            });
            availableCategories.value.push(newCategory);
            toastStore.addToast({
              type: 'success',
              message: `Categorie "${newCategory.name}" toegevoegd`,
            });
            return newCategory;
          },
          onUpdate: async (payload) => {
            const updatedCategory = await updateCategory(payload.id, {
              name: payload.name,
              slug: payload.slug || slugifyCategoryName(payload.name),
              color: payload.color || '#3B82F6',
            });
            availableCategories.value = availableCategories.value.map((category) =>
              category.id === updatedCategory.id ? updatedCategory : category
            );
            toastStore.addToast({
              type: 'success',
              message: `Categorie "${updatedCategory.name}" bijgewerkt`,
            });
            return updatedCategory;
          },
          onDelete: async (payload) => {
            await deleteCategory(payload.id);
            availableCategories.value = availableCategories.value.filter(
              (category) => category.id !== payload.id
            );
            productForm.categoryIds = productForm.categoryIds.filter((id) => id !== payload.id);
            toastStore.addToast({
              type: 'success',
              message: `Categorie "${payload.name}" verwijderd`,
            });
            return true;
          },
          onClose: () => uiStore.closeModal(modalId),
        });
      };

      const handleCreateCategory = async (newOption) => {
        // Always drop the temporary slug value emitted by MultiSelectInput;
        // we re-select by the real category id below.
        productForm.categoryIds = productForm.categoryIds.filter((id) => id !== newOption.value);

        // A category with this name/slug may already exist. Reuse it instead of
        // asking the backend to create a duplicate (which fails with a generic error).
        const slug = slugifyCategoryName(newOption.label);
        const existing = availableCategories.value.find(
          (category) =>
            slugifyCategoryName(category.name) === slug ||
            (category.slug && category.slug === slug)
        );
        if (existing) {
          const alreadySelected = productForm.categoryIds.includes(existing.id);
          if (!alreadySelected) {
            productForm.categoryIds.push(existing.id);
          }
          toastStore.addToast({
            type: 'info',
            message: alreadySelected
              ? `Categorie "${existing.name}" is al toegevoegd`
              : `Categorie "${existing.name}" bestaat al en is geselecteerd`,
          });
          return;
        }

        try {
          const categoryData = {
            name: newOption.label,
            slug,
            color: '#3B82F6',
          };

          const newCategory = await createCategory(categoryData);

          // Add to available categories
          availableCategories.value.push(newCategory);

          // Automatically select the new category
          if (!productForm.categoryIds.includes(newCategory.id)) {
            productForm.categoryIds.push(newCategory.id);
          }

          toastStore.addToast({
            type: 'success',
            message: `Categorie "${newCategory.name}" aangemaakt`,
          });
        } catch (error) {
          console.error('Error creating category:', error);
          toastStore.addToast({
            type: 'error',
            message: 'Er is een fout opgetreden bij het aanmaken van de categorie',
          });
        }
      };

      const getOptionTypeLabel = (type) => {
        const labels = {
          number: 'Nummer invoer',
          'single-choice': 'Keuzelijst',
          'multi-choice': 'Keuzelijst',
          toggle: 'Aan/uit schakelaar',
          text: 'Tekstveld',
          notice: 'Informatiemelding',
          image: 'Afbeelding',
        };
        return labels[type] || type;
      };

      // Get full option description with details (range for numbers, count for choices)
      const getOptionDescription = (option) => {
        const typeLabel = getOptionTypeLabel(option.type);

        if (option.type === 'number') {
          const min = option.numberConfig?.min ?? 0;
          const max = option.numberConfig?.max ?? '∞';
          return `${typeLabel} · ${min}-${max}`;
        }

        if (option.type === 'single-choice' || option.type === 'multi-choice') {
          const choicesCount = option.choiceConfig?.choices?.length || 0;
          return `${typeLabel} · ${choicesCount} opties`;
        }

        if (option.type === 'toggle') {
          const price = option.toggleConfig?.price || 0;
          return price ? `${typeLabel} · +€${price}` : typeLabel;
        }

        if (option.type === 'text') {
          const variant =
            option.textConfig?.variant === 'long-answer' ? 'Lang antwoord' : 'Kort antwoord';
          return `${typeLabel} · ${variant}`;
        }

        if (option.type === 'notice') {
          const variant = option.noticeConfig?.variant || 'info';
          return `${typeLabel} · ${variant}`;
        }

        if (option.type === 'image') {
          return `${typeLabel}${option.imageConfig?.src ? ' · ingesteld' : ''}`;
        }

        return typeLabel;
      };

      const getPricingTypeLabel = (type) => {
        const labels = {
          per_unit: 'Per Eenheid',
          fixed: 'Vaste Prijs',
          formula: 'Formule',
          matrix: 'Matrix (B×H)',
        };
        return labels[type] || 'Per Eenheid';
      };

      const catalogPreviewProduct = computed(() => ({
        id: productForm.id || 'catalog-preview',
        title: inputs.value.title?.binding || productForm.title || '',
        description: inputs.value.description?.binding || '',
        image_url: inputs.value.imageUrl?.binding || '',
        imageUrl: inputs.value.imageUrl?.binding || '',
        price: Number(inputs.value.price?.binding) || 0,
        options: productForm.options || [],
        option_groups: productForm.optionGroups || [],
        optionGroups: productForm.optionGroups || [],
        use_matrix_pricing: !!productForm.useMatrixPricing,
        useMatrixPricing: !!productForm.useMatrixPricing,
        pricing_matrix: productForm.pricingMatrix,
        pricingMatrix: productForm.pricingMatrix,
        pricing_deltas: productForm.pricingDeltas || [],
        pricingDeltas: productForm.pricingDeltas || [],
        computed_fields: productForm.computedFields || [],
        computedFields: productForm.computedFields || [],
        choice_constraints: productForm.choiceConstraints || [],
        choiceConstraints: productForm.choiceConstraints || [],
        cross_option_rules: productForm.crossOptionRules || [],
        crossOptionRules: productForm.crossOptionRules || [],
        auto_include_rules: productForm.autoIncludeRules || [],
        autoIncludeRules: productForm.autoIncludeRules || [],
        vat_rate: productForm.vatRate,
        vatRate: productForm.vatRate,
        prices_include_vat: productForm.pricesIncludeVat,
        pricesIncludeVat: productForm.pricesIncludeVat,
      }));

      return {
        breadcrumbs,
        isNewProduct,
        actualIsNewProduct,
        productForm,
        productId,
        selectedSupplierName,
        saving,
        currentFeed,
        // Tab navigation
        activeTab,
        configurationCount,
        // Validation
        inputs,
        onValidate,
        normalizeStandardHoursInput,
        goBack,
        saveProduct,
        bulkUpdateCurrentProductPrice,
        isCatalogMaster,
        catalogPreviewOpen,
        catalogPreviewProduct,
        publishingCatalog,
        publishCatalogMaster,
        catalogMasterDescription,
        catalogPriceBasis,
        catalogCategory,
        catalogCategoryOptions,
        catalogPriceBasisOptions,
        catalogPriceBasisHint,
        catalogInstallationId,
        onCatalogAgreementSaved,
        onCatalogRulesSaved,
        catalogMatrixDetached,
        catalogBasisLabel,
        catalogPricingNotice,
        matrixEditing,
        matrixSaving,
        matrixEditorKey,
        startMatrixEdit,
        cancelMatrixEdit,
        saveMatrixOverride,
        resetMatrixOverride,
        catalogOrderMapping,
        catalogSupplierName,
        onCatalogMappingSave,
        volumeBasisFieldOptions,
        // Product proposal attachments
        proposalAttachmentInputRef,
        isProposalAttachmentUploading,
        triggerProposalAttachmentUpload,
        handleProposalAttachmentUpload,
        removeProposalAttachment,
        // Margin & VAT (Phase 2)
        showAdvancedVat,
        vatRateOptions,
        vatBasisOptions,
        productDisplayBasisOptions,
        productPriceLabel,
        productSalePriceLabel,
        handleProductDisplayBasisChange,
        handleLegacyProductBasisChange,
        marginPreview,
        minMarginAboveTarget,
        addUpsell,
        editUpsell,
        removeUpsell,
        getUpsellKey,
        formatUpsellPrice,
        addProductOption,
        copyProductOptionsFromOther,
        editProductOption,
        removeProductOption,
        editProductOptionById,
        removeProductOptionById,
        handleMatrixPricingUpdate,
        matrixPricingSubtitle,
        // Option group management
        groupExpanded,
        ungroupedOptions,
        getOptionsInGroup,
        toggleGroup,
        isAutoFillEligible,
        isAutoFillOn,
        autoFillSiblingLabels,
        toggleAutoFill,
        // Upsell group management
        upsellGroupExpanded,
        ungroupedUpsells,
        getUpsellsInGroup,
        toggleUpsellGroup,
        addUpsellGroup,
        editUpsellGroup,
        removeUpsellGroup,
        // Drag & drop (options)
        draggingOptionId,
        dropIndicator,
        handleDragStart,
        handleDragEnd,
        handleDragOverOption,
        handleDragLeaveOption,
        handleDropOnOption,
        handleDropOnGroup,
        // Drag & drop (upsells)
        draggingUpsellId,
        upsellDropIndicator,
        handleUpsellDragStart,
        handleUpsellDragEnd,
        handleDragOverUpsell,
        handleDragLeaveUpsell,
        handleDropOnUpsell,
        handleDropOnUpsellGroup,
        addOptionGroup,
        editOptionGroup,
        removeOptionGroup,
        // Category management
        availableCategories,
        categoryOptions,
        handleCreateCategory,
        openCategoryModal,
        getOptionTypeLabel,
        getOptionDescription,
        getPricingTypeLabel,
        // Supplier management
        supplierOptions,
        unitOptions,
      };
    },
  });
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .product-detail-page {
    min-height: 100vh;
    background: var(--slate-50);

    &__breadcrumb {
      margin: 20px 0 1rem 0;
    }

    &--preview {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 26rem;
      gap: 1rem;
      align-items: start;

      @include lg-down {
        display: block;
      }
    }

    &__preview {
      position: sticky;
      top: 1rem;
      max-height: calc(100vh - 2rem);
      overflow: auto;
      margin: 1rem 1rem 1rem 0;
      padding: 1rem;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
      @include shadow('sm');

      @include lg-down {
        position: static;
        max-height: none;
        margin: 0 1rem 1rem;
      }
    }

    &__preview-note {
      margin: 0 0 1rem;
      font-size: 0.8125rem;
      line-height: 1.45;
      color: var(--slate-600);
    }
  }

  .catalog-assortment-wrap {
    margin-bottom: 1.5rem;
  }

  // Product Tabs overrides
  .product-tabs {
    :deep(.tabs__content) {
      padding: 0;
      margin-top: 1rem;
    }
  }

  .catalog-matrix-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    background: var(--slate-50);
    @include fontSize('sm');

    &--detached {
      border-color: var(--amber-200);
      background: var(--amber-50);
    }

    &__text {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
      color: var(--slate-700);
    }

    &__actions {
      display: flex;
      flex-shrink: 0;
      gap: 0.5rem;
    }

    @include sm-down {
      flex-direction: column;
      align-items: stretch;
    }
  }

  // Catalog product: options and cells come from the supplier version.
  .tab-content--locked,
  .catalog-locked {
    pointer-events: none;
    opacity: 0.7;
    user-select: none;
  }

  .tab-content {
    animation: fadeIn 0.2s ease;
  }

  .product-proposal-attachments {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--slate-200);

    &__title {
      margin: 0 0 0.25rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-800);
    }
  }

  .attachment-list {
    list-style: none;
    margin: 0.75rem 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    &__item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
      color: var(--slate-700);
      font-size: 0.875rem;
    }

    &__name {
      flex: 1;
      color: var(--slate-700);
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      @include hover {
        text-decoration: underline;
      }
    }

    &__remove {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: none;
      border-radius: 4px;
      cursor: pointer;
      color: var(--slate-400);

      @include hover {
        background-color: var(--slate-200);
        color: var(--slate-600);
      }
    }

    &__input {
      display: none;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .product-detail-page__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    padding: 1.5rem;
    background: white;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
  }

  .product-detail-page__title h1 {
    margin: 0 0 0.5rem 0;
    font-size: 1.875rem;
    font-weight: 700;
    color: var(--slate-900);
  }

  .product-detail-page__subtitle {
    margin: 0;
    color: var(--slate-600);
  }

  .product-detail-page__actions {
    display: flex;
    gap: 0.75rem;
  }

  .product-form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
      font-weight: 500;
      color: var(--slate-700);
      font-size: 0.875rem;
    }

    input,
    textarea,
    select {
      padding: 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
      font-size: 0.875rem;

      &:focus {
        outline: none;
        border-color: var(--blue-500);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }

    textarea {
      resize: vertical;
      min-height: 80px;
    }
  }

  .form-checkboxes {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--slate-700);
    cursor: pointer;
  }

  .upsell-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .upsell-item {
    display: flex;
    gap: 0.75rem;
    align-items: center;

    input {
      flex: 1;
      padding: 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
    }
  }

  .upsells-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .upsell-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    background: var(--white);
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--slate-300);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &--ghost {
      opacity: 0.5;
      border: 2px dashed var(--brand-500);
    }

    &__drag-handle {
      cursor: grab;
      color: var(--slate-400);
      display: flex;
      align-items: center;
      flex-shrink: 0;

      &:active {
        cursor: grabbing;
      }
    }

    &__image {
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      border-radius: 0.375rem;
      overflow: hidden;
      background: var(--slate-100);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--slate-400);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      flex: 1;
      min-width: 0;
    }

    &__name {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__description {
      font-size: 0.8125rem;
      color: var(--slate-500);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;
    }

    &__price {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--green-500, #059669);
      white-space: nowrap;
    }
  }

  // Upsell Group Styles
  .upsell-groups-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .upsell-group-section {
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    background: white;
    overflow: hidden;

    &--ungrouped {
      border-style: dashed;
      background: var(--slate-50);
    }

    &--ghost {
      opacity: 0.5;
      border: 2px dashed var(--brand-500);
    }
  }

  .upsell-group-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--slate-50);
    border-bottom: 1px solid var(--slate-200);
  }

  .upsell-group-drag-handle {
    cursor: grab;
    color: var(--slate-400);
    display: flex;
    align-items: center;

    &:active {
      cursor: grabbing;
    }
  }

  .upsell-group-header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    cursor: pointer;

    h4 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    .icon-rotated {
      transform: rotate(-90deg);
      transition: transform 0.2s ease;
    }
  }

  .upsell-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: var(--slate-200);
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--slate-600);
  }

  .upsell-group-header-actions {
    display: flex;
    gap: 0.25rem;
  }

  .upsell-group-content {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .upsell-group-empty {
    padding: 1.5rem;
    text-align: center;
    color: var(--slate-400);
    font-size: 0.875rem;
    border: 2px dashed var(--slate-200);
    border-radius: 6px;
  }

  .draggable-upsells-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-height: 40px;

    &--empty {
      min-height: 60px;
      border: 2px dashed var(--slate-200);
      border-radius: 8px;
      background: var(--slate-50);
    }
  }

  .upsell-card-wrapper {
    position: relative;
  }

  .upsell-card--dragging {
    opacity: 0.5;
    border: 2px dashed var(--brand-500);
  }

  .upsell-group-header--ungrouped {
    background: var(--slate-100);
    border-bottom: 1px solid var(--slate-200);

    .upsell-group-header-left {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  // Matrix Pricing Styles
  .matrix-toggle-card {
    background: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;

    &__content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &__title {
      font-weight: 600;
      font-size: 0.9375rem;
      color: var(--slate-900);
    }

    &__subtitle {
      font-size: 0.8125rem;
      color: var(--slate-500);
    }
  }

  .options-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .empty-state-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
  }

  .option-groups-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .option-group-section {
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    background: white;
    overflow: hidden;

    &--ungrouped {
      border-style: dashed;
      background: var(--slate-50);
    }

    &--ghost {
      opacity: 0.5;
      border: 2px dashed var(--brand-500);
    }
  }

  .option-group-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--slate-100);
    border-bottom: 1px solid var(--slate-200);
    cursor: pointer;
    user-select: none;

    &--ungrouped {
      background: transparent;
      cursor: default;
    }
  }

  .group-drag-handle {
    cursor: grab;
    color: var(--slate-400);
    padding: 0.25rem;
    display: flex;
    align-items: center;

    &:hover {
      color: var(--slate-600);
    }

    &:active {
      cursor: grabbing;
    }
  }

  .group-header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;

    h4 {
      margin: 0;
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    .icon-rotated {
      transform: rotate(-90deg);
      transition: transform 0.2s ease;
    }
  }

  .group-header-actions {
    display: flex;
    gap: 0.25rem;
  }

  .option-count {
    background: var(--slate-200);
    color: var(--slate-600);
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
  }

  .option-group-content {
    padding: 1rem;
    min-height: 60px;
  }

  .draggable-options-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 40px;

    &--empty {
      min-height: 60px;
      border: 2px dashed var(--slate-200);
      border-radius: 8px;
      background: var(--slate-50);
    }
  }

  .option-card-wrapper {
    position: relative;
  }

  .drop-indicator {
    height: 3px;
    background: var(--brand-500);
    border-radius: 2px;
    margin: -0.375rem 0;
    position: relative;
    z-index: 10;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      background: var(--brand-500);
      border-radius: 50%;
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }
  }

  .empty-drop-hint {
    color: var(--slate-400);
    font-size: 0.85rem;
    text-align: center;
    padding: 1rem;
    margin: 0;
  }

  .options-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .option-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid var(--slate-200);
    border-radius: 12px;
    padding: 0.875rem 1rem;
    background: white;
    cursor: move;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--slate-300);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    }

    &__drag-handle {
      cursor: grab;
      color: var(--slate-300);
      display: flex;
      align-items: center;
      flex-shrink: 0;

      &:active {
        cursor: grabbing;
      }

      &:hover {
        color: var(--slate-500);
      }
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: var(--slate-100);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--brand-600);
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      flex: 1;
      min-width: 0;
    }

    &__name {
      font-weight: 600;
      font-size: 0.9375rem;
      color: var(--slate-900);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__description {
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;
      margin-left: auto;
    }

    &__badge {
      font-size: 0.8125rem;
      font-weight: 500;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;

      &--required {
        color: var(--amber-600);
        background: var(--amber-100);
        border: 1px solid var(--amber-500);
      }

      &--internal {
        color: var(--blue-600);
        background: var(--blue-100);
        border: 1px solid var(--blue-500);
      }

      &--external {
        color: var(--purple-600, #9333ea);
        background: var(--purple-100, #f3e8ff);
        border: 1px solid var(--purple-500, #a855f7);
      }

      &--packing {
        color: var(--green-600);
        background: var(--green-100);
        border: 1px solid var(--green-500);
      }

      &--hidden {
        color: var(--slate-600);
        background: var(--slate-100);
        border: 1px solid var(--slate-400);
      }

      // Clickable auto-fill toggle: dashed outline when off (available),
      // solid blue when on (identical single-choice fields fill each other in the funnel).
      &--autofill {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        cursor: pointer;
        color: var(--blue-600);
        background: transparent;
        border: 1px dashed var(--blue-500);
        transition: all 0.15s ease;

        &:hover {
          background: var(--blue-50, #eff6ff);
        }

        &.is-on {
          color: white;
          background: var(--blue-600);
          border: 1px solid var(--blue-600);
        }
      }
    }

    // Ghost state - shows where item will be dropped
    &--ghost {
      opacity: 0.4;
      background: var(--slate-100);
      border: 1px dashed var(--brand-500);

      * {
        opacity: 0;
      }
    }

    // Chosen state - item being dragged
    &--chosen {
      opacity: 0.8;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 1000;
    }

    // Auto-fill on: blue left accent (inset keeps the 12px radius intact).
    &--autofill {
      border-color: var(--blue-200, #bfdbfe);
      box-shadow: inset 3px 0 0 var(--blue-500);

      &:hover {
        box-shadow:
          inset 3px 0 0 var(--blue-500),
          0 2px 4px rgba(0, 0, 0, 0.04);
      }
    }

    // Drag state - item is being dragged
    &--drag {
      opacity: 0.5;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    // Native dragging state
    &--dragging {
      opacity: 0.5;
      transform: scale(0.98);
    }
  }

  .option-group-select {
    width: 140px;
    min-width: 140px;
    font-size: 0.75rem;
  }

  .section-description {
    margin: 0 0 1rem 0;
    color: var(--slate-600);
    font-size: 0.875rem;
  }

  .attributes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .attribute-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
      font-weight: 500;
      color: var(--slate-700);
      font-size: 0.875rem;
    }

    input,
    select {
      padding: 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
      font-size: 0.875rem;

      &:focus {
        outline: none;
        border-color: var(--blue-500);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }

  // Categories selector styles
  .categories-selector {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .no-categories {
    text-align: center;
    padding: 2rem;
    border: 2px dashed var(--slate-300);
    border-radius: 8px;
    color: var(--slate-500);

    p {
      margin: 0 0 1rem 0;
    }
  }

  .categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
  }

  .category-checkbox {
    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: var(--blue-300);
        background: var(--blue-50);
      }

      input[type='checkbox']:checked + .category-color + span {
        font-weight: 600;
      }
    }
  }

  .category-color {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .add-category-btn {
    align-self: flex-start;
  }

  // Pricing info styles
  .pricing-info {
    // margin-top: 0.5rem;
    // padding: 0.75rem;
    // background: var(--blue-50);
    // border-radius: 6px;
    // border: 1px solid var(--blue-200);

    .pricing-type {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      background: var(--blue-100);
      color: var(--blue-800);
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    .pricing-details {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      span {
        font-size: 0.875rem;
        color: var(--slate-700);
      }

      .formula {
        font-family: 'Monaco', 'Consolas', monospace;
        background: var(--slate-100);
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.75rem;
      }
    }
  }

  // ---------------------------------------------------------------
  // Margin & VAT card (Phase 2 — 2026-05-12)
  // ---------------------------------------------------------------
  .margin-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    &__preview {
      display: block;
      margin-top: 0.25rem;
      padding: 0.4375rem 0.5625rem;
      background: var(--brand-50);
      border-left: 2px solid var(--brand-400, #60a5fa);
      border-radius: 0.25rem;
      color: var(--brand-700);
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }
  }

  .help-text--warning {
    display: inline-flex;
    align-items: center;
    gap: 0.3125rem;
    color: var(--amber-700, #b45309);
    background: var(--amber-50, #fffbeb);
    padding: 0.375rem 0.5625rem;
    border-radius: 0.375rem;
    border: 1px solid var(--amber-200, #fde68a);
    margin-top: 0.25rem;
    font-weight: 500;
  }

  .margin-advanced {
    margin-top: 0.5rem;
    border-top: 1px dashed var(--slate-200);
    padding-top: 0.875rem;

    &__toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      background: transparent;
      border: 0;
      padding: 0.25rem 0;
      color: var(--slate-700);
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;

      .icon {
        transition: transform 0.15s ease;
      }

      &--open .icon {
        transform: rotate(90deg);
      }

      &:hover {
        color: var(--slate-900);
      }
    }

    &__body {
      margin-top: 0.75rem;
      padding: 0.875rem;
      background: var(--slate-50);
      border-radius: 0.5rem;
    }
  }

  .vat-basis-option__label {
    display: block;
    margin: 0 0 0.25rem;
    font-weight: 600;
    font-size: 0.8125rem;
    color: var(--slate-700);
  }

  .vat-basis-radio {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.625rem;

    &__option {
      display: flex;
      gap: 0.625rem;
      align-items: flex-start;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--white);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        background-color 0.15s ease;

      &:hover {
        border-color: var(--slate-300);
      }

      input[type='radio'] {
        margin: 0.1875rem 0 0;
        accent-color: var(--brand-500);
        cursor: pointer;
      }

      &--checked {
        border-color: var(--brand-400, #60a5fa);
        background: var(--brand-50);
      }
    }

    &__title {
      display: block;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__hint {
      display: block;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
      margin-top: 0.125rem;
    }
  }
</style>
